﻿import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { AuthContext } from '../context/AuthContext.jsx';
import Tier2Result from './Tier2Result.jsx';
import { getCompliment } from './complimentMap.js';
import { BASE, pointsApi, UNLOCK_REPORT_AMOUNT } from '../api.js';
import { checkAndResize } from '../utils/imageResize.js';
// 统一 cookie 鉴权 fetch（同源 /api 自动带共享域 cookie；跨源带 credentials 转发）
async function fetchWithCookie(url, opts = {}) {
  return fetch(url, { credentials: 'include', ...opts });
}

import { composeShareCard, shareImage, fetchInviteInfo } from '../utils/makeShareCard.js';
import { removeStorageItem, STORAGE_KEYS } from '../utils/storage.js';
import CapturePhotoUpload from './CapturePhotoUpload.jsx';
import Tier2PhotoUpload from './Tier2PhotoUpload.jsx';
import GenderConfirmCard from './GenderConfirmCard.jsx';

const RESULT_ITEMS = [
  { key: 'faceShape',        label: '脸型',         icon: '◎' },
  { key: 'skinType',         label: '肤质',         icon: '◉' },
  { key: 'eyebrowShape',     label: '眉形',         icon: '❖', placeholder: '上传照片后生成' },
  { key: 'eyeShape',         label: '眼型',         icon: '◐', placeholder: '上传照片后生成' },
  { key: 'threeFiveRatio',   label: '三庭五眼',     icon: '☰', placeholder: '上传照片后生成' },
  { key: 'symmetry',         label: '五官对称度',   icon: '⚖', placeholder: '上传照片后生成' },
];

const AD_DURATION_SEC = 5;


const TIER3_QUESTIONS = [
  { key: 'reportGender', title: '这次为哪位定制？' },
  { key: 'makeupStyle', title: '你想走哪种风格大类？' },
  { key: 'shareMakeup', title: '最想做哪个分享妆容？' },
  { key: 'scenario', title: '这个妆容用在什么场景？' },
  { key: 'skillLevel', title: '你的化妆手法是？' },
  { key: 'timeCost', title: '愿意花多久打扮自己？' },
];

const TIER3_FALLBACK_OPTIONS = {
  makeupStyle: ['日常伪素颜', '职场通勤', '约会纯欲', '韩系欧尼', '新中式国风', '复古港风', '泰式浓颜', '欧美派对'],
  shareMakeup: ['白开水素颜', '早八5分钟', '学生裸妆', '元气蜜桃'],
  scenario:    ['日常上班上学', '闺蜜逛街下午茶', '约会见crush', '日常拍照发圈', '海边度假旅行', '相亲见长辈', '面试正式商务', '见家长家庭聚会', '生日/朋友聚会', '婚礼/音乐节派对'],
  skillLevel:  ['新手入门', '有一定基础', '熟练进阶'],
  timeCost:    ['5分钟极简', '15分钟日常', '30分钟以上精致']};

// 细分/分享妆容随「风格大类」联动（第2题选项）：大类 → 该大类下的分享妆容列表
// 数据来源：用户提供的「大类 / 细分妆容」对照表；后台可经 /tier3/questionnaire-options 的 shareMakeup 维度覆盖默认（见下方 options 解析）
const MAKEUP_SHARE_MAP = {
  '日常伪素颜': ['白开水素颜', '早八5分钟', '学生裸妆', '元气蜜桃'],
  '职场通勤': ['OL干练', '面试专业', '商务知性'],
  '约会纯欲': ['纯欲蜜桃', '氛围感红茶', '樱花甜妹'],
  '韩系欧尼': ['水光玻璃肌', '无辜狗狗眼', '微醺腮红', '橘棕日常'],
  '新中式国风': ['唐风花钿', '宋制清雅', '现代新中式'],
  '复古港风': ['港星红唇', '复古雀斑', '浓眉猫眼'],
  '泰式浓颜': ['野生浓眉', '晒红辣妹', '小麦健康肌'],
  '欧美派对': ['烟熏Cut Crease', 'Y2K千禧', '音乐节亮片'],
};
// 大类未选/未知时的第2题兜底选项
const MAKEUP_SHARE_FALLBACK = MAKEUP_SHARE_MAP['日常伪素颜'];
// ===== 第 1 题「性别」：本次专属报告只用于这一次，写入 quiz_answers.reportGender =====
// 取值 female/male/other；决定下方第 2 题大类走女性妆库(makeupStyle) 还是男性妆库(maleStyle)。
const REPORT_GENDER_OPTIONS = ['女性', '男性', '其他'];
const GENDER_TO_LIB = { '女性': 'female', '男性': 'male', '其他': 'female' }; // 其他→按女性库兜底，可手切男性

// 男性风格大类（第 2 题选项，男性时显示）：与 makeup_prompt_library.json v2.3.0 male_categories 一致
const MALE_CATEGORY_MAP = {
  '男士日常淡妆':   ['素颜霜提气色', '清爽眉眼提气色'],
  '男士韩系欧巴':   ['清爽水光欧巴', '奶狗无辜眼'],
  '男士轻熟商务':   ['轻熟质感商务', '盐系熟男'],
  '男士国风古风':   ['国风公子/侠客'],
  '男士舞台浓妆':   ['舞台浓妆上镜', '主持/镜头轻上镜'],
};
const MALE_CATEGORY_LIST = Object.keys(MALE_CATEGORY_MAP);
const MALE_CATEGORY_FALLBACK = MALE_CATEGORY_MAP['男士日常淡妆'];

function navigateBack() {
  window.location.href = 'https://auth.meijian.top/home';
}

function navigateToCapture() {
  window.history.pushState({}, '', '/capture');
  window.dispatchEvent(new PopStateEvent('popstate'));
}

// 将 Unix 秒时间戳格式化为 YYYY-MM-DD（北京时间展示）
function formatExpireDate(tsSec) {
  if (!tsSec) return '';
  const d = new Date(tsSec * 1000);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function AdOverlay({ duration, onComplete }) {
  const [remain, setRemain] = useState(duration);
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);
  useEffect(() => {
    if (duration <= 0) { onCompleteRef.current(); return; }
    const t = setInterval(() => {
      setRemain((r) => {
        if (r <= 1) { clearInterval(t); setTimeout(() => onCompleteRef.current(), 0); return 0; }
        return r - 1;
      });
    }, 1000);
    return () => { clearInterval(t); };
  }, [duration]);
  return (
    <div className="tier2-ad-overlay">
      <div className="tier2-ad-inner">
        <div className="tier2-ad-icon">📺</div>
        <p className="tier2-ad-title">广告播放中</p>
        <p className="tier2-ad-sub">效果美图即将呈现，请稍候…</p>
        <div className="tier2-ad-countdown">{remain}s</div>
        <div className="tier2-ad-bar-wrap">
          <div className="tier2-ad-bar-fill" style={{ width: ((duration - remain) / duration) * 100 + '%' }} />
        </div>
      </div>
    </div>
  );
}

const T3_PROD_GROUP_LABELS = { base: '底妆', eyes: '眼妆', lips: '唇妆', cheeks: '腮红' };
const T3_SECTION_STEPS = [
  { key: 'advice',  label: '整体建议',     icon: '✦' },
  { key: 'style',   label: '风格与场景融合', icon: '🎯' },
  { key: 'steps',   label: '步骤指南',     icon: '📋' },
  { key: 'tips',    label: '贴心提示',     icon: '⚠' },
  { key: 'time',    label: '时间提醒',     icon: '⏱' },
  { key: 'influencer',label: '化妆达人匹配', icon: '👩' },
];


// ── 化妆达人匹配卡片 ──────────────────────────────────────────────────────────
// 调用 /api/influencers/match 获取与用户面部特征最匹配的化妆达人（Top2）
function InfluencerMatchCard() {
  const [matches, setMatches] = useState(null);
  const [error, setError] = useState(null);
  const [emptyReason, setEmptyReason] = useState(null); // "no_tier1" | "no_match"
  const { token } = useContext(AuthContext);

  useEffect(() => {
    if (!token) { setError("未登录"); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithCookie(BASE + "/influencers/match");
        if (cancelled) return;
        const data = await res.json();
        if (!res.ok) {
          // 404 = 该用户还没有 tier1 拍照数据（no_tier1_data）；
          // 静默成占位，不弹红字，但区分"没做初识"与"有数据却没匹配到"
          if (res.status === 404) { setMatches([]); setEmptyReason(data && data.error === "no_tier1_data" ? "no_tier1" : "no_match"); return; }
          setError(data.message || "加载失败");
          return;
        }
        const arr = data.matches || [];
        setMatches(arr);
        setEmptyReason(arr.length === 0 ? "no_match" : null);
      } catch (e) {
        if (!cancelled) setError("网络异常，请重试");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (error) {
    return (
      <div className="t3-card t3-card--influencer">
        <h2 className="t3-card-title">👩 化妆达人匹配</h2>
        <p className="t3-influencer-empty">{error}</p>
      </div>
    );
  }

  if (matches === null) {
    return (
      <div className="t3-card t3-card--influencer">
        <h2 className="t3-card-title">👩 化妆达人匹配</h2>
        <p className="t3-influencer-loading">正在为你匹配最合适的化妆达人…</p>
      </div>
    );
  }

  if (matches.length === 0) {
    return (
      <div className="t3-card t3-card--influencer">
        <h2 className="t3-card-title">👩 化妆达人匹配</h2>
        <p className="t3-influencer-empty">
          {emptyReason === "no_tier1"
            ? "完成「初识」拍照分析后，为你匹配最合适的化妆达人"
            : "暂无匹配的化妆达人，更多达人入驻中，敬请期待"}
        </p>
      </div>
    );
  }

  return (
    <div className="t3-card t3-card--influencer">
      <h2 className="t3-card-title">👩 化妆达人匹配</h2>
      <div className="t3-influencer-list">
        {matches.map((inf) => (
          <div key={inf.id} className="t3-influencer-card">
            {inf.makeupPhotoUrl ? (
              <img src={inf.makeupPhotoUrl} alt={inf.nickname} className="t3-influencer-avatar" loading="lazy" decoding="async" />
            ) : (
              <div className="t3-influencer-avatar t3-influencer-avatar--placeholder">
                {(inf.nickname || "达").charAt(0)}
              </div>
            )}
            <div className="t3-influencer-info">
              <span className="t3-influencer-name">
                {inf.nickname}
                {inf.score != null && (
                  <span className="t3-influencer-score">匹配度 {Math.round(inf.score * 100)}%</span>
                )}
              </span>
              {inf.bio && <span className="t3-influencer-bio">{inf.bio}</span>}
              {inf.platform && <span className="t3-influencer-platform">{inf.platform}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}



import { buildStepsWithAI, calcPhaseTotals, GROUP_TO_DIM, PRODUCTS } from "../utils/tier3StepEngine.js";
import { buildProfile } from "../utils/tier3ToProfile.js";


function Tier3Report({ content, onRefresh, onShare, shareLoading, shareDone, photoUrl, enrichPending, aiImageUrl, showAiImage, onRegenAiImage, aiImageRegenBusy, aiImageExhausted, aiRateLimitLiftAt }) {
  const { overallAdvice, stepByStep, productRecs, tips, timeWarning, styleNote, emphasisOverride, undertone, lipPref, skinType, faceShape, eyeShape } = content;

  // 场景 key
  const scenarioRaw = content._scenario || "日常通勤";
  const scenarioMap = { "约会聚会": "约会", "婚礼派对": "晚宴", "拍照旅行": "拍照" };
  const sceneKey = scenarioMap[scenarioRaw] || scenarioRaw;

  // 构建 profile（只用 content 里 AI 返回的字段 + scenario，不依赖 tier3Answers）
  const profile = {
    undertone: undertone || "冷调",
    lipPref: lipPref || "豆沙色系",
    faceShape: faceShape || "鹅蛋脸",
    skinType: skinType || "混合皮",
    eyeShape: eyeShape || "杏眼",
    skillLevel: "有一定基础",
    timeBudget: "15 分钟日常",
    emphasisOverride: emphasisOverride || null,
  };

  // 9 步骨架（AI 无对应步骤时本地引擎兜底）
  const steps = buildStepsWithAI(profile, sceneKey, content);
  const phase1 = steps.filter(s => s.phase === 1);
  const phase2 = steps.filter(s => s.phase === 2);

  // 阶段二是否还在生成（AI 还没返回 06-09 步）
  const hasP2Steps = Array.isArray(stepByStep)
    ? stepByStep.some(it => { const n = parseInt(String(it.step || it.no || ""), 10); return n >= 6 && n <= 9; })
    : false;
  const p2Loading = !hasP2Steps;

  // 灯泡弹窗状态
  const [prodLayerOpen, setProdLayerOpen] = useState(false);
  const [activeProdDim, setActiveProdDim] = useState(null); // 当前灯泡对应分类，弹窗只展示这一类
  const [activePhaseTab, setActivePhaseTab] = useState("1");
  // 妆前/妆后图 lightbox：{ url, label } —— 缩略图聚焦脸，点击弹层看全图
  const [baLightbox, setBaLightbox] = useState(null);
  // 妆后 AI 图加载失败（404/破图）标记：显示占位 + 限次自动重生成，避免"破图静默"
  const [baImgErr, setBaImgErr] = useState(false);
  const autoRegenRef = useRef(0);
  // 拿到新的有效 URL（成功重生成/刷新）时复位坏图标记
  useEffect(() => { if (aiImageUrl) setBaImgErr(false); }, [aiImageUrl]);
  // 限流窗口倒计时：aiRateLimitLiftAt > 现在 → 按钮置灰并显示"~N 分钟后可重试"
  const [rlNow, setRlNow] = useState(Date.now());
  useEffect(() => {
    if (!aiRateLimitLiftAt || aiRateLimitLiftAt <= Date.now()) { setRlNow(Date.now()); return; }
    const iv = setInterval(() => setRlNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [aiRateLimitLiftAt]);
  const rlRemaining = aiRateLimitLiftAt > rlNow ? Math.ceil((aiRateLimitLiftAt - rlNow) / 1000) : 0;
  // 图片加载失败 → 标记坏图；最多自动触发 2 次重生成（避免失败死循环），之后交给手动按钮
  const handleAiImgError = useCallback(() => {
    setBaImgErr(true);
    if (onRegenAiImage && autoRegenRef.current < 2) {
      autoRegenRef.current += 1;
      onRegenAiImage();
    }
  }, [onRegenAiImage]);

  // 产品数据：productRecs 结构 { base:[...], eyes:[...], lips:[...], cheeks:[...] }
  // 归一化为 { dim: { items: [...] } }，灯泡弹窗按 group.items 读取
  const dimLabels = { base: "底妆", eyes: "眼妆", lips: "唇妆", cheeks: "腮红" };
  const productsToShow = (() => {
    if (!productRecs || typeof productRecs !== "object") return null;
    const out = {};
    for (const [dim, items] of Object.entries(productRecs)) {
      if (Array.isArray(items) && items.length > 0) out[dim] = { items };
    }
    return Object.keys(out).length > 0 ? out : null;
  })();

  // 各 step.group → productRecs.dim 映射（决定灯泡出现在哪些步骤）
  // 直接复用 tier3StepEngine 导出的 GROUP_TO_DIM


  // 计算阶段总时长
  // 优先用 AI 实际每步耗时（step.time）按阶段求和；AI 缺失该步时才回退 calcPhaseTotals 公式
  const sumStepMinutes = (arr) => { let t = 0, found = false; for (const st of arr) { const m = String(st.time || "").match(/(\d+)\s*分钟/); if (m) { t += parseInt(m[1], 10); found = true; } } return found ? t : null; };
  const _calcTotals = calcPhaseTotals(profile);
  const _p1Sum = sumStepMinutes(phase1);
  const _p2Sum = sumStepMinutes(phase2);
  const phaseTotals = { p1: _p1Sum ?? _calcTotals.p1, p2: _p2Sum ?? _calcTotals.p2 };

  let styleTag = styleNote ? (styleNote.match(/^[^，,。；·]+/) || [styleNote])[0] : "专属定制";
  if (/^[?\uFFFD\s]+$/.test(String(styleTag).trim())) styleTag = "专属定制";
  const _sceneRawVal = scenarioMap[scenarioRaw] || scenarioRaw || "今日妆容";
  // 防御：历史脏数据/编码事故可能让 scenario 变成连续 ?，展示时回落"今日妆容"
  const sceneLabel = /^[?\uFFFD\s]+$/.test(String(_sceneRawVal).trim()) ? "今日妆容" : _sceneRawVal;

  // 渲染单个步骤（含灯泡）
  function renderStep(step, isLast) {
    const dim = GROUP_TO_DIM[step.group] || "base";
    const hasProducts = productsToShow && productsToShow[dim] && productsToShow[dim].items.length > 0;
    const diffCls = "diff-" + (step.difficulty || "中");
    return (
      <div key={step.no} className="timeline-item">
        <div className="timeline-left">
          <div className="timeline-node">{step.no}</div>
          {/* 每个编号下方都画贯穿竖线（含最后一步），时间线连贯 */}
          <div className="timeline-line" />
        </div>
        <div className="timeline-card">
          <div className="step-head-row">
            <p className="step-title">{step.name}</p>
            {hasProducts && (
              <button type="button" className="step-bulb step-bulb-right" title="查看为你精选"
                onClick={() => { setActiveProdDim(dim); setProdLayerOpen(true); }}>💡</button>
            )}
          </div>
          <div className="step-meta-row">
            <span className="step-time">⏱ {step.time}</span>
            <span className={"step-difficulty " + diffCls}>{step.difficulty}难度</span>
          </div>

          {step.detail && step.detail.length > 0 && (
            <ul className="timeline-detail">
              {step.detail.map((d, i) => <li key={i}>{d}</li>)}
            </ul>
          )}

          {step.focus && (
            <div className="timeline-focus">
              <span className="focus-label">🎯 为什么这样推荐</span>
              {step.focus}
            </div>
          )}

          {step.avoid && step.avoid.length > 0 && (
            <div className="timeline-avoid">
              <span className="avoid-label">⚠ 避雷提示</span>
              <ul>
                {step.avoid.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="t3-page tier3-v2">

      {/* Hero：用户照片 + 风格关键词 + 场景 */}
      <div className="t3-hero">
        <div className="t3-hero-top">
          <div className="t3-hero-text-col">
            <div className="t3-hero-badge">✦ 专属美妆方案 ✦</div>
            <h1 className="t3-hero-title">专属美妆方案</h1>
            <div className="t3-hero-subtitle">
              <span className="t3-hero-subtitle-tag">{styleTag}</span>
              <span className="t3-hero-subtitle-dot">·</span>
              <span className="t3-hero-subtitle-scene">{sceneLabel}</span>
            </div>
          </div>
          {photoUrl && (
            <div className="t3-hero-photo-wrap">
              <img className="t3-hero-photo" src={photoUrl} alt="你的照片" />
            </div>
          )}
        </div>

        {overallAdvice && (
          <div className="t3-hero-advice">
            <div className="t3-hero-advice-label">✦ 方案解读</div>
            <p className="t3-hero-advice-text">{overallAdvice}</p>
          </div>
        )}

        <div className="t3-progress">
          <div className="t3-progress-bg">
            <div className="t3-progress-fill" style={{ width: "100%" }} />
          </div>
          <span className="t3-progress-label">9 步专属方案已生成</span>
        </div>
      </div>

      {/* 阶段选项卡 */}
      <div className="t3-card" style={{ padding: "16px 14px" }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          <button
            type="button"
            onClick={() => setActivePhaseTab("1")}
            style={{
              flex: 1, padding: "10px 0", borderRadius: 10, fontSize: 13, fontWeight: 700,
              cursor: "pointer", border: activePhaseTab === "1" ? "2px solid #d84b96" : "1px solid #f0d9e8",
              background: activePhaseTab === "1" ? "#fdf2f8" : "#fff",
              color: activePhaseTab === "1" ? "#d84b96" : "#8a7f76",
              transition: "all .15s",
            }}
          >
            阶段一 · 完美裸妆奠基
            <div style={{ fontSize: 11, fontWeight: 500, color: activePhaseTab === "1" ? "#d84b9688" : "#b6a8a0", marginTop: 2 }}>
              伪素颜 · 约 {phaseTotals.p1} 分钟
            </div>
          </button>
          <button
            type="button"
            onClick={() => setActivePhaseTab("2")}
            style={{
              flex: 1, padding: "10px 0", borderRadius: 10, fontSize: 13, fontWeight: 700,
              cursor: "pointer", border: activePhaseTab === "2" ? "2px solid #d84b96" : "1px solid #f0d9e8",
              background: activePhaseTab === "2" ? "#fdf2f8" : "#fff",
              color: activePhaseTab === "2" ? "#d84b96" : "#8a7f76",
              transition: "all .15s",
            }}
          >
            阶段二 · 一键升级精致美妆
            <div style={{ fontSize: 11, fontWeight: 500, color: activePhaseTab === "2" ? "#d84b9688" : "#b6a8a0", marginTop: 2 }}>
              紧致美妆 · 约 {phaseTotals.p2} 分钟
            </div>
          </button>
        </div>

        {activePhaseTab === "1" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {phase1.map((s, i) => renderStep(s, i < phase1.length - 1))}
          </div>
        )}

        {activePhaseTab === "2" && (
          p2Loading ? (
            <div className="p2-loading">
              <span className="p2-loading-icon">⏳</span>
              <span>阶段二内容生成中，请稍候…</span>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {phase2.map((s, i) => renderStep(s, i < phase2.length - 1))}
            </div>
          )
        )}
      </div>

      {/* 妆前/妆后对比图（放在步骤骨架之后、贴心提示之前） */}
      {(photoUrl || aiImageUrl) && (
        <div className="t3-card t3-card--before-after">
          <h2 className="t3-card-title">妆前妆后对比</h2>
          <p className="ba-sub">
            {aiImageUrl && showAiImage !== false
              ? "上方为你上传的照片，下方为 AI 生成的妆后效果参考"
              : "上方为你上传的照片；妆后 AI 效果图见下方"}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {photoUrl && (
              <div style={{ textAlign: "center" }}>
                <div style={{ display: "inline-block", position: "relative", cursor: "zoom-in" }}>
                  <img
                    src={photoUrl}
                    alt="妆前"
                    onClick={() => setBaLightbox({ url: photoUrl, label: "妆前" })}
                    style={{ width: 140, height: 180, objectFit: "cover", objectPosition: "center 30%", borderRadius: 12, border: "2px solid #f0d9e8", display: "block" }}
                  />
                  <div style={{ position: "absolute", bottom: 6, left: "50%", transform: "translateX(-50%)", fontSize: 11, background: "rgba(0,0,0,0.55)", color: "#fff", padding: "2px 8px", borderRadius: 8, whiteSpace: "nowrap" }}>妆前 · 点查看大图</div>
                </div>
              </div>
            )}
            {aiImageUrl && showAiImage !== false && (
              <div style={{ textAlign: "center" }}>
                <div style={{ display: "inline-block", position: "relative", cursor: "zoom-in" }}>
                  <img
                    src={aiImageUrl}
                    alt="妆后"
                    onClick={() => setBaLightbox({ url: aiImageUrl, label: "妆后" })}
                    onError={handleAiImgError}
                    style={{ width: 140, height: 180, objectFit: "cover", objectPosition: "center 30%", borderRadius: 12, border: "2px solid #f0d9e8", display: "block" }}
                  />
                  <div style={{ position: "absolute", bottom: 6, left: "50%", transform: "translateX(-50%)", fontSize: 11, background: "rgba(0,0,0,0.55)", color: "#fff", padding: "2px 8px", borderRadius: 8, whiteSpace: "nowrap" }}>妆后 · 点查看大图</div>
                </div>
              </div>
            )}
            {(!aiImageUrl || baImgErr) && showAiImage !== false && (
              <div style={{
                textAlign: "center", padding: "20px 16px", borderRadius: 12,
                background: "#faf3f6", color: "#8a7f76", fontSize: 13, lineHeight: 1.6,
                border: "1px dashed #f0d9e8",
              }}>
                {baImgErr
                  ? "⚠️ 妆后 AI 图加载失败，点击下方按钮重新生成"
                  : "🕐 妆后 AI 效果图生成中，完成后会自动显示（无需刷新）"}
                {(baImgErr || aiImageExhausted) && onRegenAiImage && (
                  <div style={{ marginTop: 12 }}>
                    <button
                      type="button"
                      onClick={onRegenAiImage}
                      disabled={aiImageRegenBusy || rlRemaining > 0}
                      style={{
                        padding: "8px 18px", borderRadius: 10, fontSize: 13, fontWeight: 600,
                        border: "1px solid #f0d9e8", background: "#fff", color: "#d84b96",
                        cursor: (aiImageRegenBusy || rlRemaining > 0) ? "not-allowed" : "pointer",
                        opacity: rlRemaining > 0 ? 0.6 : 1,
                      }}
                    >
                      {aiImageRegenBusy ? "正在生成妆后图…" : rlRemaining > 0 ? ("⏳ 限流中，约 " + Math.max(1, Math.ceil(rlRemaining / 60)) + " 分钟后可重试") : "重新生成妆后图"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

        {/* 妆前/妆后 lightbox 大图弹层 */}
        {baLightbox && (
          <div
            onClick={() => setBaLightbox(null)}
            style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.85)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 16 }}
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setBaLightbox(null); }}
              style={{ position: "absolute", top: 14, right: 14, width: 38, height: 38, borderRadius: "50%", border: "none", background: "rgba(255,255,255,0.18)", color: "#fff", fontSize: 18, cursor: "pointer" }}
            >✕</button>
            <img
              src={baLightbox.url}
              alt={baLightbox.label}
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "100%", maxHeight: "82vh", objectFit: "contain", borderRadius: 12 }}
            />
            <div style={{ marginTop: 10, color: "#fff", fontSize: 13, opacity: 0.9 }}>{baLightbox.label} · 大图</div>
          </div>
        )}

      {/* 贴心提示 */}
      {Array.isArray(tips) && tips.length > 0 && (
        <div className="t3-card t3-card--tips">
          <h2 className="t3-card-title">⚠ 贴心提示</h2>
          <ul className="t3-tips">
            {tips.map((t, i) => <li key={i} className="t3-tip">{t}</li>)}
          </ul>
        </div>
      )}

      {/* 化妆达人匹配 */}
      <InfluencerMatchCard />

      {/* 时间提醒 */}
      {timeWarning && (
        <div className="t3-time-warning">
          <span className="t3-time-warning-icon">⏱</span>
          <p className="t3-time-warning-text">{timeWarning}</p>
        </div>
      )}

      {/* 灯泡弹窗：推荐商品 */}
      {prodLayerOpen && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 999, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
          onClick={() => setProdLayerOpen(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: "16px 16px 0 0", width: "100%", maxWidth: 480,
              maxHeight: "75vh", overflowY: "auto", padding: "20px 18px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1a1a1a" }}>💡 {(activeProdDim ? (dimLabels[activeProdDim] || "为你精选") : "为你精选")}</h3>
              <button type="button" onClick={() => setProdLayerOpen(false)} style={{ border: "none", background: "#f3f4f6", borderRadius: 8, padding: "4px 10px", cursor: "pointer", fontSize: 13 }}>关闭</button>
            </div>
            {(productsToShow || Object.keys(PRODUCTS).length > 0) ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {(productsToShow ? Object.entries(productsToShow) : Object.entries(PRODUCTS)).filter(([dim]) => !activeProdDim || dim === activeProdDim).map(([dim, group]) => {
                  const items = group.items || [];
                  if (items.length === 0) return null;
                  const label = dimLabels[dim] || dim;
                  return (
                    <div key={dim}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#d84b96", marginBottom: 8 }}>{label}</div>
                      {items.map((rec, j) => {
                        const name = typeof rec === "string" ? rec : rec.name || "";
                        const reason = typeof rec === "string" ? "" : rec.reason || "";
                        const price = rec.price != null && rec.price > 0 ? rec.price : null;
                        return (
                          <div key={j} style={{ display: "flex", gap: 10, padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
                            {rec.imageUrl ? (
                              <img src={rec.imageUrl} alt={name} style={{ width: 52, height: 52, borderRadius: 8, objectFit: "cover" }} />
                            ) : (
                              <div style={{ width: 52, height: 52, borderRadius: 8, background: "#f3f0f5", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>🛍</div>
                            )}
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a" }}>{name}</div>
                              {reason && <div style={{ fontSize: 11, color: "#8a6070", marginTop: 2 }}>{reason}</div>}
                              <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                                {price != null && <span style={{ fontSize: 12, color: "#d84b96", fontWeight: 700 }}>¥{price}</span>}
                                {rec.itemUrl && (
                                  <a href={rec.itemUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: "#d84b96", textDecoration: "none" }}>淘宝查看 ↗</a>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
                {enrichPending && (
                  <div style={{ fontSize: 12, color: "#8a7f76", padding: "8px 0" }}>正在为你精选，请稍候…</div>
                )}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: "#8a7f76" }}>暂无为你精选的好物</div>
            )}
          </div>
        </div>
      )}

      {/* 底部操作 */}
      <div className="t3-footer">
        <button className="t3-refresh-btn" onClick={onRefresh}>重新生成</button>
        <button className="t3-share-btn" onClick={onShare} disabled={shareLoading}>
          {shareDone ? "✓ 已生成分享图" : "分享报告"}
        </button>
      </div>
    </div>
  );
}

export default function ReportPage() {
  const { token } = useContext(AuthContext);
  const state = window.history.state || {};
  const urlParams = new URLSearchParams(window.location.search);
  // 优先级：URL state > URL query > sessionStorage > path
  // 注意：history.state 可能在 navigate() 后被其他操作覆盖，所以兜底到 sessionStorage
  const reportId = state.reportId 
    ?? urlParams.get('id') 
    ?? sessionStorage.getItem('capture_report_id')
    ?? (window.location.pathname.match(/^\/report\/([^?]+)/)?.[1] || null);

  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab === 'tier3' || tab === '专属') return '专属';
    if (tab === '进阶') return '进阶';
    return '初识';
  });
  const [tier1Report, setTier1Report] = useState(null);
  // 中枢画像（性别/年龄）：入口确认卡 onReady 回传；tier1/tier2 报告默认带此性别
  const [hubProfile, setHubProfile] = useState(null);
  const [preview, setPreview] = useState(state.preview || sessionStorage.getItem('capture_preview') || null);
  
  // 初始化时从 sessionStorage 恢复报告数据（如果 reportId 存在）
  useEffect(() => {
    if (!reportId) return;
    const stored = sessionStorage.getItem('capture_report_' + reportId);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed && Object.keys(parsed).length > 0) {
          setTier1Report(parsed);
        }
      } catch (e) {
        console.error('[ReportPage] Failed to parse tier1 report from sessionStorage:', e);
      }
    }
  }, [reportId]);

  // Restore photo preview from R2 when tier1 report is loaded (key format: face-photos/{userId}/{reportId}.jpg)
  useEffect(() => {
    if (!reportId) return;
      /* 前端不再持有 token；照片预览改由本端接口按 reportId 解析当前用户（cookie 鉴权）*/
      // 使用正确的 R2 key 格式：face-photos/{userId}/{reportId}.jpg
      // 但前端没有 userId，所以改用 reportId 作为 key
      setPreview('/api/r2-proxy?key=face-photos/' + encodeURIComponent(reportId) + '.jpg&bucket=temp');
    }, [reportId]);
  const [openPhoto, setOpenPhoto] = useState(null);
  const [tier2Status, setTier2Status] = useState(null);
  const [tier2Content, setTier2Content] = useState(null);
  const [tier2LoadError, setTier2LoadError] = useState(null);
  const [tier2Generation, setTier2Generation] = useState(null);
  const [tier2Processing, setTier2Processing] = useState(false);
  const [tier2FacePhotoKey, setTier2FacePhotoKey] = useState(null);
  const [imgUnlockLoading, setImgUnlockLoading] = useState(false);
  const [showAd, setShowAd] = useState(false);
  const [adMode, setAdMode] = useState('image'); // 'image'=AI妆效图解锁 'tier2'=进阶报告广告解锁
  const [imgResult, setImgResult] = useState(null);
  const [retryable, setRetryable] = useState(false);
  const unlockBusyRef = useRef(false);
  const shareCardRef = useRef(null);
  const qrCanvasRef = useRef(null);
  const shareUrlRef = useRef('');
  const [shareLoading, setShareLoading] = useState(false);
  const [shareDone, setShareDone] = useState(false);
  const [adUnlockLoading, setAdUnlockLoading] = useState(false);
  // 重新拍摄解锁：看广告消耗每日 1 次名额，解锁后展示照片上传
  const [redoUnlocked, setRedoUnlocked] = useState(false);
  const [redoTier2Id, setRedoTier2Id] = useState(null);
  const [shareDailyLimitExceeded, setShareDailyLimitExceeded] = useState(false);
  const [reportValid, setReportValid] = useState(null); // null = not checked yet
  const tier2TimerRef = useRef(null); // track polling interval to prevent multiple simultaneous timers
  const tier2StuckSinceRef = useRef(null); // 看门狗：processing 起始时间
  const tier2StuckRetriesRef = useRef(0); // 重新触发次数
  const [btnColor, setBtnColor] = useState("#000000");
  // Tier3 state（兑换码资格改由 tier3RedeemCodeUsed 承载，见下方声明）
  const [tier3QuestionnaireOptions, setTier3QuestionnaireOptions] = useState(null);
  const [tier3ShowQuestionnaire, setTier3ShowQuestionnaire] = useState(false);
  const [tier3Answers, setTier3Answers] = useState({});
  const tier3AnswersRef = useRef(tier3Answers);
  useEffect(() => { tier3AnswersRef.current = tier3Answers; }, [tier3Answers]);
  const [tier3Generating, setTier3Generating] = useState(false);
  const [tier3Content, setTier3Content] = useState(null);
  // 两步交互：报告生成完先收起，点“查看报告”才展开（二层=点灯泡才显示商品）
  const [tier3ReportViewOpen, setTier3ReportViewOpen] = useState(false);
  const [tier3ReportId, setTier3ReportId] = useState(null);
  const [tier3EnrichPending, setTier3EnrichPending] = useState(false);
  const tier3EnrichFiredRef = useRef(null); // 记录已触发 enrich-products 的报告 id，避免重复
  const tier2EnrichFiredRef = useRef(0); // tier2 enrich 已触发次数（上限 3，或服务端 final 后停止）

  // 个人中心：最新一份专属（tier3）报告简要信息（专属页 scenario 回退等）
  const [myTier3, setMyTier3] = useState(null);
  const [cdNow, setCdNow] = useState(() => Date.now());
  useEffect(() => { const cdT = setInterval(() => setCdNow(Date.now()), 60000); return () => clearInterval(cdT); }, []);
  const [myTier3Archives, setMyTier3Archives] = useState([]); // 个人中心：全部专属报告档案（每份独立，不互相覆盖）
  const [archiveOpenId, setArchiveOpenId] = useState(null); // 档案详情：当前展开的报告 id
  const [archiveDetail, setArchiveDetail] = useState(null); // 档案详情内容（按报告 id 懒加载）
  const [archiveDetailLoading, setArchiveDetailLoading] = useState(false);
  const [tier3Error, setTier3Error] = useState(null);
  const [tier3RedeemCode, setTier3RedeemCode] = useState('');
  const [tier3Redeeming, setTier3Redeeming] = useState(false);
  // 兑换码核销成功后，记住该码（code 本身），生成时传给 /tier3/generate 精确关联
  const [tier3RedeemCodeUsed, setTier3RedeemCodeUsed] = useState(null);
  const tier3RedeemCodeUsedRef = useRef(null);
  // 专属报告积分余额 / 扣减中标记
  const [tier3PointsBalance, setTier3PointsBalance] = useState(null);
  const [tier3PointsConsume, setTier3PointsConsume] = useState(false);
  // 本会话已积分解锁专属报告（扣积分成功后的内存态；积分是"每次都要扣"，刷新后不再恢复资格，重新生成需再次扣积分）
  // 仅当次会话有效；刷新页面后状态清零，回到积分解锁引导页（需再次扣积分）。
  const [tier3PointsUnlocked, setTier3PointsUnlocked] = useState(false);
  // 专属报告（tier3）照片上传：问卷完成 → 上传照片 → 开始生成
  const [tier3Photo, setTier3Photo] = useState(null);
  const [tier3PhotoKey, setTier3PhotoKey] = useState(null);
  const [tier3ContentPhotoUrl, setTier3ContentPhotoUrl] = useState(null); // 本次刚生成报告对应的照片预览 URL
  const [tier3AiImageUrl, setTier3AiImageUrl] = useState(null); // 本次报告 AI 妆效图
  const [tier3AiImageExhausted, setTier3AiImageExhausted] = useState(false); // 妆后图 3 次轮询都未出图 → 显示"重新生成"按钮
  const [tier3AiImageRegenBusy, setTier3AiImageRegenBusy] = useState(false); // "重新生成妆后图" 进行中
  const [tier3AiRateLimitLiftAt, setTier3AiRateLimitLiftAt] = useState(0); // CF 1015 限流窗口截止时间(ms)：>0 时"重新生成"按钮置灰倒计时
  const [archiveAiImageRegenBusy, setArchiveAiImageRegenBusy] = useState(false); // 个人中心档案路径"重新生成妆后图" 进行中
  const [tier3ShowAiImage, setTier3ShowAiImage] = useState(true); // 后台开关：是否显示 tier3 AI 妆效图模块
  useEffect(() => {
    fetchWithCookie(BASE + '/admin/config').then(r=>r.json()).then(d=>{
      const cfg = (d.configs||[]).find(c=>c.key==='tier3_show_ai_image');
      setTier3ShowAiImage(cfg ? cfg.value !== 'false' : true);
    }).catch(()=>{});
  }, []);
  const [tier3PhotoUploading, setTier3PhotoUploading] = useState(false);
  const [tier3PhotoError, setTier3PhotoError] = useState(null);
  const tier3PhotoInputRef = useRef(null);
  const tier3PhotoKeyRef = useRef(null);
const tier3PhotoKeyLiveRef = useRef(null);
  const tier3AiImageFiredRef = useRef(null); // 记录已补拉 ai-image 的报告 id，避免重复
  const tier3FacePhotoKeyRef = useRef(null); // 本次报告 facePhotoKey（ai-image 补拉用）
  // P1 妆后图自愈：失败后自动后台重试 2 次（30s/60s 间隔）+ 重新生成按钮 60s 冷却防连点轰炸 i2i 服务
  const tier3AiImageRetryTimersRef = useRef([]); // 自动重试定时器句柄
  const tier3AiImageRegenCooldownRef = useRef(0); // 重新生成冷却截止时间戳
  useEffect(() => () => { (tier3AiImageRetryTimersRef.current || []).forEach((t) => clearTimeout(t)); }, []);
  useEffect(() => { tier3PhotoKeyRef.current = tier3PhotoKey; tier3PhotoKeyLiveRef.current = tier3PhotoKey; }, [tier3PhotoKey]);
  const [tier3LoadError, setTier3LoadError] = useState(null);
  const [tier3PreviewText, setTier3PreviewText] = useState('');
  const [tier3PreviewLoading, setTier3PreviewLoading] = useState(true);
  const [tier3CurrentQuestionIndex, setTier3CurrentQuestionIndex] = useState(0);
  const [tier3AnswerFlash, setTier3AnswerFlash] = useState(null);
  const [showArchive, setShowArchive] = useState(false);
  const [reuploadTier1, setReuploadTier1] = useState(false);
  const tier3TimerRef = useRef(null);

  const handleReuploadTier1 = useCallback(() => {
    setReuploadTier1(true);
  }, []);

  // Load tier1 report from sessionStorage (set by Capture.jsx after analysis)
  useEffect(() => {
    if (!reportId) {
      console.log('[ReportPage] No reportId, current reportId:', reportId);
      return;
    }
    console.log('[ReportPage] Loading report for reportId:', reportId);
    const stored = sessionStorage.getItem('capture_report_' + reportId);
    console.log('[ReportPage] sessionStorage key:', 'capture_report_' + reportId, 'value:', stored ? 'found' : 'not found');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        console.log('[ReportPage] Parsed report:', parsed);
        if (parsed && Object.keys(parsed).length > 0) {
          setTier1Report(parsed);
        }
      } catch (e) {
        console.error('[ReportPage] Failed to parse tier1 report:', e);
      }
    }
  }, [reportId]);

  // Validate reportId on load — clears stale/expired IDs before any API call
  useEffect(() => {
    if (!reportId || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithCookie(BASE + '/tier1/validate?id=' + encodeURIComponent(reportId));
        const data = await res.json();
        if (!cancelled) setReportValid(data.valid ?? false);
      } catch {
        if (!cancelled) setReportValid(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reportId, token]);

  // 记住已有报告：reportId 在本地（history.state / URL ?id / sessionStorage）三处都取不到时，
  // 查该用户数据库里最新的一条初识报告（GET /reports/mine 的 tier1Report），
  // 有值则写入 sessionStorage 并直接展示报告；无值/401/网络错误则什么都不做，
  // 让页面按原逻辑落到「选择照片」流程。
  // 与上面 validate effect 互斥：validate 要求 reportId 非空且 token 存在，
  // 本 effect 要求 reportId 为空且 token 存在，同一时刻不可能同时触发。
  useEffect(() => {
    if (reportId || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithCookie(BASE + '/reports/mine');
        if (!res.ok) return; // 401/5xx/网络异常：不设置任何 state，走原「选择照片」流程
        const data = await res.json();
        if (cancelled || !data.tier1Report) return;
        const { id, report } = data.tier1Report;
        if (!id) return;
        setTier1Report(report);
        setReportValid(true);
        try {
          sessionStorage.setItem('capture_report_' + id, JSON.stringify(report));
          sessionStorage.setItem('capture_report_id', id);
        } catch {}
      } catch {
        // 网络错误/超时/解析失败：静默降级，不设置任何 state
      }
    })();
    return () => { cancelled = true; };
  }, [reportId, token]);

  // 初识报告不存在/已失效：清除过期的报告指针，避免首页再次指向失效报告，
  // 让「初识」标签直接展示拍照上传页
  useEffect(() => {
    if (reportValid === false) {
      setPreview(null);
      try { sessionStorage.removeItem('capture_report_id'); } catch {}
    }
  }, [reportValid]);

  // Load tier2 generation status on mount
  // 进阶报告独立：没有初识报告（reportId 为空）也查询该用户最新的进阶报告
  useEffect(() => {
    if (!token) return; // P0：token 未就绪时空跑会 401，加守卫避免重复请求
    let cancelled = false;
    (async () => {
      try {
        const qs = reportId ? '?tier1ReportId=' + encodeURIComponent(reportId) : '';
        const res = await fetchWithCookie(BASE + '/tier2/status' + qs);
        if (!res.ok) throw new Error('请求失败: ' + res.status);
        const data = await res.json();
        if (!cancelled) {
          setTier2Generation(data);
          setTier2Status(data);
          if (data.facePhotoKey) setTier2FacePhotoKey(data.facePhotoKey);
          if (data.generationStatus === 'ready' && data.content) {
            setTier2Content(data.content);
            // 打开历史报告：若商品尚未补齐（enriched=false），异步补图/链接（幂等；final=true 时服务端已收尾，不再重试）
            if (!data.content.enriched && data.tier2ReportId) {
              fetchWithCookie(BASE + '/tier2/enrich-products', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reportId: data.tier2ReportId }) })
                .then((r) => (r.ok ? r.json() : null))
                .then((en) => { if (en && en.productRecs) setTier2Content((prev) => (prev ? { ...prev, productRecs: en.productRecs, enriched: en.enriched, _enrichFinal: en.final || en.enriched === true } : prev)); })
                .catch(() => {});
            }
          }
          // pending + 关联初识报告（历史数据）：主动触发生成一次
          // 独立 pending（无初识关联）：等用户上传照片，不再自动触发，避免失败循环
          if (data.generationStatus === 'pending' && data.sourceTier1ReportId) {
            setTier2Generation({ ...data, generationStatus: 'processing' });
            setTier2Status({ ...data, generationStatus: 'processing' });
            fetchWithCookie(BASE + '/tier2/generate', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ reportId: data.tier2ReportId })
            }).catch(() => {});
          }
        }
      } catch {
        if (!cancelled) {
          setTier2Generation({ generationStatus: 'not_found' });
          setTier2Status({ generationStatus: 'not_found' });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [reportId, token]);

  // Use a ref for generation state so the interval callback always reads current values.
  // 只依赖状态字符串（而非整个对象），避免每次轮询都重建定时器
  const tier2GenerationRef = useRef(tier2Generation);
  useEffect(() => { tier2GenerationRef.current = tier2Generation; }, [tier2Generation]);

  useEffect(() => {
    if (tier2Generation?.generationStatus !== 'processing') return;
    setTier2Processing(true);
    let aborted = false;
    let timer = null;
    let delay = 1500; // P1-2：指数退避起点（1.5s），无进度时逐级放大到 10s，减少空转
    const stop = () => { aborted = true; if (tier2TimerRef.current) { clearTimeout(tier2TimerRef.current); tier2TimerRef.current = null; } };
    const scheduleNext = () => { if (aborted) return; delay = Math.min(delay * 2, 10000); timer = setTimeout(runOnce, delay); tier2TimerRef.current = timer; };
    const runOnce = async () => {
      if (aborted) return;
      const gen = tier2GenerationRef.current;
      if (!gen?.tier2ReportId) { scheduleNext(); return; }
      try {
        const res = await fetchWithCookie(BASE + '/tier2/status?tier2Id=' + encodeURIComponent(gen.tier2ReportId), {
          });
        if (aborted) return;
        if (!res.ok) throw new Error('请求失败: ' + res.status);
        const data = await res.json();
        if (aborted) return;
        if (data.generationStatus === 'pending') {
          // pending：只有关联初识报告的历史数据才自动续生成；独立报告停止轮询，界面转为上传照片入口
          if (data.sourceTier1ReportId) {
            setTier2Generation({ ...data, generationStatus: 'processing' });
            setTier2Status({ ...data, generationStatus: 'processing' });
            delay = 1500; // 重新触发生成 → 恢复快节奏
            fetchWithCookie(BASE + '/tier2/generate', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ reportId: data.tier2ReportId })
            }).catch(() => {});
            scheduleNext();
          } else {
            setTier2Generation(data);
            setTier2Status(data);
            stop();
            setTier2Processing(false);
          }
          return;
        }
        if (data.generationStatus === 'processing') {
          // 看门狗：卡 processing 超 4 分 40 秒（与服务端 5 分钟孤儿规则对齐）：
          // 关联初识的报告最多重新触发 3 次；独立报告无初识数据可生成，不再自动重触发，
          // 直接标记 failed，界面引导重新上传照片（杜绝"失败→重新生成→再失败"死循环）
          if (tier2StuckSinceRef.current === null) tier2StuckSinceRef.current = Date.now();
          if (Date.now() - tier2StuckSinceRef.current > 280000) {
            tier2StuckRetriesRef.current += 1;
            if (data.sourceTier1ReportId && tier2StuckRetriesRef.current <= 3) {
              tier2StuckSinceRef.current = Date.now();
              console.log('[ReportPage] tier2 stuck in processing, re-triggering generation, attempt ' + tier2StuckRetriesRef.current);
              delay = 1500;
              fetchWithCookie(BASE + '/tier2/generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reportId: data.tier2ReportId })
              }).catch(() => {});
              scheduleNext();
              return;
            }
            setTier2Generation({ ...data, generationStatus: 'failed' });
            setTier2Status({ ...data, generationStatus: 'failed' });
            stop();
            setTier2Processing(false);
          } else {
            // 仍在生成：保持看门狗计时，按退避节奏继续轮询
            scheduleNext();
          }
          return;
        }
        tier2StuckSinceRef.current = null;
        tier2StuckRetriesRef.current = 0;
        setTier2Generation(data);
        setTier2Status(data);
        if (data.facePhotoKey) setTier2FacePhotoKey(data.facePhotoKey);
        if (data.content) setTier2Content(data.content);
        // enrich 已拆到独立端点：ready 时商品先占位（AI 名+参考价），异步补图/链接
        // 有上限：最多 3 次，或收到 final=true（服务端已收尾/淘宝持续失败）即停止，避免联盟 API 挂掉时无限重打
        const _enrichAttempts = (tier2Content && tier2Content._enrichAttempts != null) ? tier2Content._enrichAttempts : 0;
        const _enrichFinal = !!(tier2Content && tier2Content._enrichFinal);
        if (data.content && !data.content.enriched && !data.content._enrichFinal && data.tier2ReportId && _enrichAttempts < 3 && !_enrichFinal) {
          tier2EnrichFiredRef.current = (_enrichAttempts + 1);
          fetchWithCookie(BASE + '/tier2/enrich-products', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reportId: data.tier2ReportId }) })
            .then((r) => (r.ok ? r.json() : null))
            .then((en) => { if (en && en.productRecs) setTier2Content((prev) => (prev ? { ...prev, productRecs: en.productRecs, enriched: en.enriched, _enrichFinal: en.final || en.enriched === true, _enrichAttempts: (_enrichAttempts + 1) } : prev)); })
            .catch(() => {});
        }
        stop();
        setTier2Processing(false);
      } catch {
        // keep polling on transient errors
        scheduleNext();
      }
    };
    timer = setTimeout(runOnce, delay);
    tier2TimerRef.current = timer;
    return () => {
      aborted = true;
      if (tier2TimerRef.current) {
        clearTimeout(tier2TimerRef.current);
        tier2TimerRef.current = null;
      }
      setTier2Processing(false);
    };
  }, [reportId, token, tier2Generation?.generationStatus]);


  // Fetch tier2_btn_color from admin config on mount
  useEffect(() => {
    let cancelled = false;
    fetchWithCookie(BASE + '/config/tier2_btn_color')
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (!cancelled && data?.value) setBtnColor(data.value);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!token) return;
    if (activeTab !== '专属') return;
    let cancelled = false;
    async function load() {
      // 各请求独立兜底：任何接口失败都不置 loadError（否则整页白屏）
      if (cancelled) return;
      try {
        const optionsRes = await fetchWithCookie(BASE + '/tier3/questionnaire-options').catch(() => null);
        if (optionsRes && optionsRes.ok && !cancelled) {
          const optionsData = await optionsRes.json();
          setTier3QuestionnaireOptions(optionsData.options || {});
        }
      } catch { /* 忽略 */ }
      try {
        // 3s 超时兜底：弱网下 config 请求可能挂几十秒，解锁预览不该被一个 0.5kB 的文案请求拖住
        const previewPromise = fetchWithCookie(BASE + '/config/tier3_preview_text').catch(() => null);
        const previewRes = await Promise.race([
          previewPromise,
          new Promise((resolve) => setTimeout(() => resolve(null), 3000)),
        ]);
        if (previewRes && previewRes.ok && !cancelled) {
          const previewData = await previewRes.json();
          setTier3PreviewText(previewData.value || '专属报告为你提供个性化深度分析，涵盖整体建议、步骤指南和推荐产品。');
        } else if (!cancelled) {
          setTier3PreviewText('专属报告为你提供个性化深度分析，涵盖整体建议、步骤指南和推荐产品。');
        }
      } catch { /* 忽略 */ }
      if (!cancelled) setTier3PreviewLoading(false);
    }
    void load();
    return () => { cancelled = true; };
  }, [activeTab, token]);

  // 专属报告解锁：查询积分余额 + 是否已积分解锁（本端 tier3_points_unlock 落库）。
  // 余额用于"消耗积分解锁"按钮可用性；pointsUnlocked 用于刷新/换设备后恢复"已解锁资格"，
  // 不再依赖当次会话的内存 ref，避免"已扣积分但状态丢失"。
  useEffect(() => {
    if (!token) return;
    if (activeTab !== '专属') return;
    let cancelled = false;
    // 本端 /api/points/balance 已 proxy 直连中枢（token 失效时中枢直接返回 401）
    pointsApi.getBalance().then((val) => {
      if (!cancelled) setTier3PointsBalance(val);
    }).catch(() => {
      if (!cancelled) setTier3PointsBalance(null);
    });
    return () => { cancelled = true; };
  }, [activeTab, token]);
    // 兑换码核销成功（tier3RedeemCodeUsed 非空）时，自动进入定制问卷
    useEffect(() => {
      if (!token) return;
      if (activeTab !== '专属') return;
      if (tier3RedeemCodeUsed && !tier3ShowQuestionnaire && !tier3Content && !tier3PointsUnlocked) {
        setTier3ShowQuestionnaire(true);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tier3RedeemCodeUsed, token, activeTab]);

  // 个人中心：加载用户的全部专属（tier3）报告档案（纯新增：每生成一份都是一条独立档案，不互相覆盖）
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithCookie(BASE + '/tier3/content');
        if (!res.ok) { console.warn('[tier3/content] fetch failed:', res.status); return; }
        const data = await res.json();
        if (!cancelled && Array.isArray(data.reports) && data.reports.length > 0) {
          setMyTier3Archives(data.reports);
          const latest = data.reports[0];
          setMyTier3({ style: latest.style, scenario: latest.scenario, expireAt: latest.expireAt, id: latest.id, photoUrl: latest.photoUrl });
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [token]); // P0：showArchive 仅是 UI 状态，移出依赖避免重复拉取 /tier3/content

  // 个人中心档案：点击某份专属报告 → 拉取该报告的完整内容（详情查看）
  useEffect(() => {
    if (!archiveOpenId || !token) return;
    let cancelled = false;
    setArchiveDetailLoading(true);
    setArchiveDetail(null);
    (async () => {
      try {
        const res = await fetchWithCookie(BASE + '/tier3/report-id?id=' + encodeURIComponent(archiveOpenId));
        if (!res.ok) { console.warn('[tier3/report-id] fetch failed:', res.status); return; }
        const data = await res.json();
        if (!cancelled) setArchiveDetail(data);
        // 档案详情：恢复该报告的 AI 妆效图（步骤与商品之间展示）
        if (data && data.aiImageUrl) {
          setTier3AiImageUrl(data.aiImageUrl);
        } else if (data && data.id && data.facePhotoKey && !data.aiImageUrl) {
          // 档案路径补拉一次（幂等；已有图直接回，缺 reportId/photoKey 或 404 时静默占位）
          fetchWithCookie(BASE + '/tier3/ai-image', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reportId: data.id, photoKey: data.facePhotoKey }) })
            .then((r) => r.ok ? r.json() : null)
            // ai-image 返回的 aiImageUrl 已是完整已编码的 /api/r2-proxy?key=...&bucket=temp URL，直接用（不能再包一层，否则 404）
            .then((ai) => { if (!cancelled && ai && ai.aiImageUrl) setTier3AiImageUrl(ai.aiImageUrl); })
            .catch(() => {});
        }
        if (data && data.photoUrl) setTier3ContentPhotoUrl(data.photoUrl);
        // Step 2：二层界面进入时按需补全淘宝商品（幂等；已补全则秒回，失败不影响报告主体）
        fetchWithCookie(BASE + '/tier3/enrich-products', { method: 'POST', headers: { 'Content-Type': 'application/json'}, body: JSON.stringify({ reportId: archiveOpenId }) })
          .then((r) => (r.ok ? r.json() : null))
          .then((en) => { if (!cancelled && en && en.productRecs) setArchiveDetail((prev) => (prev && prev.id === archiveOpenId ? { ...prev, content: { ...(prev.content || {}), productRecs: en.productRecs } } : prev)); })
          .catch(() => {});
      } catch {}
      if (!cancelled) setArchiveDetailLoading(false);
    })();
    return () => { cancelled = true; };
  }, [archiveOpenId, token]);
  const handleTier3Submit = useCallback(async () => {
    if (tier3Generating || !token) return;
    const dims = ['reportGender', 'makeupStyle', 'shareMakeup', 'scenario', 'skillLevel', 'timeCost'];
    const missing = dims.filter((d) => !tier3Answers[d]);
    if (missing.length > 0) {
      setTier3Error('请选择所有选项');
      return;
    }
    setTier3Generating(true);
    setTier3Error(null);
    setTier3Content(null);
    try {
      const res = await fetchWithCookie(BASE + '/tier3/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json'},
        body: JSON.stringify({
          tier1ReportId: reportId,
          questionnaireAnswers: tier3Answers,
          facePhotoKey: tier3PhotoKey || undefined,
          redeemCode: tier3RedeemCodeUsedRef.current || undefined})});
      const data = await res.json();
      if (res.ok && data.aiImageUrl) setTier3AiImageUrl(BASE + '/r2-proxy?key=' + encodeURIComponent(data.aiImageUrl) + '&bucket=temp');
      if (!res.ok) {
        if (res.status === 403 && data.error === 'no_redeem_code') {
          setTier3RedeemCodeUsed(null);
          tier3RedeemCodeUsedRef.current = null;
          setTier3Error('暂无解锁资格，请使用积分或兑换码解锁后重试');
        } else if (data.retryable) {
          setTier3Error('生成失败，未消耗资格，可重新尝试');
        } else {
          setTier3Error(data?.error || '请求失败 ' + res.status);
        }
        // 成功才扣费模型：生成前未扣积分，生成失败无需退回（本就没有扣过）
      } else {
                // 若 AI 未给出具体商品名（productRecs 各维度为空），补一套默认品类骨架，
        // 保证二层「查看推荐商品」有内容可匹配；已命名则保持 AI 原样
        let finalContent = data.content || {};
        const recs = finalContent.productRecs;
        const isEmptyRecs = !recs || Object.keys(recs).length === 0 ||
          Object.values(recs).every((v) => !Array.isArray(v) || v.length === 0);
        if (isEmptyRecs) {
          finalContent = {
            ...finalContent,
            productRecs: {
              base: [{ name: "气垫粉底", reason: "轻薄持妆，通勤百搭" }],
              eyes: [{ name: "大地色眼影盘", reason: "自然提神" }, { name: "眼线胶笔", reason: "放大眼睛" }],
              lips: [{ name: "豆沙色唇釉", reason: "提升气色" }],
              cheeks: [{ name: "膏状腮红", reason: "自然红润" }]}};
        }
        setTier3Content(finalContent);

        // 生成成功：清除"待退回"标记（积分已真实消耗，不再退回）+ 中枢赠送积分（grant-tier3 幂等去重）
        tier3PointsConsumedPendingRef.current = false;
        if (data.id) pointsApi.grantTier3(data.id).catch(() => {});
        // Step 2：报告主体已出，二层商品按需补全（幂等；失败不阻断展示）
        if (data.id) {
          fetchWithCookie(BASE + '/tier3/enrich-products', { method: 'POST', headers: { 'Content-Type': 'application/json'}, body: JSON.stringify({ reportId: data.id }) })
            .then((r) => (r.ok ? r.json() : null))
            .then((en) => { if (en && en.productRecs) setTier3Content((prev) => (prev ? { ...prev, productRecs: en.productRecs } : prev)); })
            .catch(() => {});
        }
        setTier3ContentPhotoUrl(tier3PhotoKeyLiveRef.current ? "/api/r2-proxy?key=" + encodeURIComponent(tier3PhotoKeyLiveRef.current) + "&bucket=temp" : null);
        // 兑换码已消耗，清掉 redeemCodeUsed（不可重复使用）
        setTier3RedeemCodeUsed(null);
        tier3RedeemCodeUsedRef.current = null;
      }
    } catch (e) {
      setTier3Error('生成超时或服务异常，请点下方按钮重试');
    } finally {
      setTier3Generating(false);
    }
  }, [reportId, tier3Generating, token]);
  // 消耗积分解锁：动作成功那一刻调本端 /api/points/consume（代理 auth-center，价格/去重服务端定），
  // 成功后：1) 强制刷新余额（治"积分不变"）；2) 把资格落库（本端 tier3_points_unlock，刷新不丢）；3) 进入问卷。
  // 真正生成走 /api/tier3/generate（fromPoints=true，不再重复扣积分）。
  const tier3PointsGrantedRef = useRef(false);
  // 本次会话已"扣积分但报告尚未生成成功"（解锁成功→问卷/照片→开始分析）。生成失败时据此退回积分。
  const tier3PointsConsumedPendingRef = useRef(false);
  // 每次点击生成一个新 related_id（本会话内不变，确保同一次"解锁→问卷→生成"只扣一次积分，
  // 用户刷新页面后 related_id 重新生成，下次点击再扣一次——符合"每次解锁都要扣积分"的产品逻辑）。
  const tier3RelatedIdRef = useRef(null);
  const getRelatedId = () => {
    if (!tier3RelatedIdRef.current) {
      tier3RelatedIdRef.current = 'tier3_report_' + (token ? token.slice(0, 8) : 'anon') + '_' + Date.now();
    }
    return tier3RelatedIdRef.current;
  };

  const handleTier3UnlockByPoints = useCallback(async () => {
    if (tier3PointsConsume || !token) return;
    setTier3PointsConsume(true);
    setTier3Error(null);
    try {
      // 成功才扣费模型：解锁(进入问卷)这一步【不扣积分】，只校验余额是否足够；
      // 真正扣 6 积分发生在「报告生成成功」时（handleTier3DoSubmit，按报告 id 去重），生成失败则积分原封不动。
      let bal = tier3PointsBalance;
      try { const fresh = await pointsApi.getBalance(); if (typeof fresh === 'number') bal = fresh; } catch {}
      if (bal == null || bal < UNLOCK_REPORT_AMOUNT) {
        // 积分不够 → 不解锁(不进入问卷)，提示去充值
        if (typeof bal === 'number') setTier3PointsBalance(bal);
        setTier3Error('积分余额不足（需 ' + UNLOCK_REPORT_AMOUNT + ' 积分），请前往购买积分');
        window.setTimeout(() => {
          window.location.href = 'https://auth.meijian.top/buy-points?from=' + encodeURIComponent(window.location.href);
        }, 300);
        return;
      }
      // 余额足够 → 放行进入问卷（此步不扣费，扣费推迟到生成成功）
      tier3PointsGrantedRef.current = true;
      tier3PointsConsumedPendingRef.current = false; // 不再预付，无需退款标记
      setTier3PointsUnlocked(true);
      setTier3PointsBalance(bal);
      setTier3ShowQuestionnaire(true);
      setTier3Error(null);
    } catch {
      setTier3Error('积分校验失败，请重试');
    } finally {
      setTier3PointsConsume(false);
    }
  }, [tier3PointsConsume, token, tier3PointsBalance]);

  // 专属报告照片选择（拍照/相册/文件）
  // 关键修复：纯 web 浏览器（非 Capacitor 原生 App）里 @capacitor/camera 的 getPhoto 会
  // "resolve 但 dataUrl 为空"（不抛异常），导致选完照片"没反应"（预览/开始分析都不出现）。
  // 只有 Capacitor 原生壳才走相机；web 一律直接开系统文件选择器（可靠路径，已验证）。
  const isNativeCapacitor = () =>
    typeof window !== 'undefined' &&
    !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  const openTier3FilePicker = (mode) => {
    setTier3PhotoError(null);
    const input = tier3PhotoInputRef.current;
    if (!input) return;
    try {
      // web 浏览器：拍照走后置相机，相册走系统图库
      if (mode === 'camera') input.setAttribute('capture', 'environment');
      else input.removeAttribute('capture');
    } catch { /* capture 属性个别浏览器不支持，忽略 */ }
    input.value = '';
    input.click();
  };
  const pickWithCapacitorCamera = async (source) => {
    try {
      setTier3PhotoError(null);
      const { Camera } = await import('@capacitor/camera');
      const r = await Camera.getPhoto({ quality: 85, allowEditing: false, resultType: 1, source });
      // 仅当真正拿到 base64 才用；否则落回文件选择器
      if (r && r.dataUrl) { setTier3Photo(r.dataUrl); return true; }
    } catch { /* 原生不可用/异常 → 落回 web 文件选择器 */ }
    return false;
  };
  const handleTier3PhotoPickCamera = async () => {
    if (isNativeCapacitor()) {
      const ok = await pickWithCapacitorCamera('camera');
      if (ok) return;
    }
    openTier3FilePicker('camera');
  };
  const handleTier3PhotoPickGallery = async () => {
    if (isNativeCapacitor()) {
      const ok = await pickWithCapacitorCamera('photos');
      if (ok) return;
    }
    openTier3FilePicker('gallery');
  };
  const handleTier3PhotoInputChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setTier3PhotoError(null);
      const dataUrl = await checkAndResize(file);
      setTier3Photo(dataUrl);
      setTier3PhotoError(null);
    } catch {
      setTier3PhotoError('照片处理失败，请换一张试试');
    }
  };
  const handleTier3PhotoSubmit = async () => {
    if (!tier3Photo || tier3PhotoUploading || !token) return;
    setTier3PhotoUploading(true);
    setTier3PhotoError(null);
    try {
      const res = await fetchWithCookie(BASE + '/tier3/upload-photo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ photo: tier3Photo })});
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setTier3PhotoError(json?.message || json?.error || '照片上传失败，请重试');
        return;
      }
      const newKey = json.tier3FacePhotoKey || null;
      tier3PhotoKeyLiveRef.current = newKey;
      setTier3PhotoKey(newKey);
      setTier3Photo(null);
      // 照片上传成功 → 开始分析生成
      void handleTier3DoSubmit();
    } catch {
      setTier3PhotoError('网络异常，请重试');
    } finally {
      setTier3PhotoUploading(false);
    }
  };

  const handleTier3Back = useCallback(() => {
    setTier3CurrentQuestionIndex((prev) => Math.max(0, prev - 1));
  }, []);
  const handleTier3DoSubmit = useCallback(async () => {
    // 新一轮生成：收起已展开的报告，重置报告 id 与补全状态
    setTier3ReportViewOpen(false);
    setTier3ReportId(null);
    setTier3EnrichPending(false);
    tier3EnrichFiredRef.current = null;
    if (tier3Generating || !token) {
        return;
    }
    const dims = ['reportGender', 'makeupStyle', 'shareMakeup', 'scenario', 'skillLevel', 'timeCost'];
    const currentAnswers = tier3AnswersRef.current;
    const missing = dims.filter((d) => !currentAnswers[d]);
    if (missing.length > 0) {
      setTier3Error('请选择所有选项');
        return;
    }
    const livePhotoKey = tier3PhotoKeyLiveRef.current ?? tier3PhotoKeyRef.current;
    if (!livePhotoKey) {
      setTier3Error('请先上传照片');
      return;
    }
    // 解锁资格检查：兑换码核销成功（tier3RedeemCodeUsedRef 非空）或积分解锁（tier3PointsGranted/tier3PointsUnlocked）
    const hasReedemCode = !!tier3RedeemCodeUsedRef.current;
    if (!hasReedemCode && !tier3PointsGrantedRef.current && !tier3PointsUnlocked) {
      setTier3Error('暂无解锁资格，请使用积分或兑换码解锁后重试');
      return;
    }
    setTier3Generating(true);
    setTier3Error(null);
    setTier3Content(null);
    try {
      // 兑换码路径不传 fromPoints；积分路径传 fromPoints=true
      const usePoints = !hasReedemCode && (tier3PointsGrantedRef.current || tier3PointsUnlocked);
      // 成功才扣费模型：生成前再卡一次余额——积分路径若此刻余额不足，本次不出报告（避免"报告出了却没扣成钱"）
      if (usePoints) {
        let genBal = tier3PointsBalance;
        try { const fb = await pointsApi.getBalance(); if (typeof fb === "number") genBal = fb; } catch {}
        if (genBal == null || genBal < UNLOCK_REPORT_AMOUNT) {
          setTier3Generating(false);
          setTier3PointsBalance(typeof genBal === "number" ? genBal : null);
          setTier3Error("积分余额不足（需 " + UNLOCK_REPORT_AMOUNT + " 积分），本次未生成，请充值后再试");
          window.setTimeout(() => {
            window.location.href = "https://auth.meijian.top/buy-points?from=" + encodeURIComponent(window.location.href);
          }, 300);
          return;
        }
      }
      const res = await fetchWithCookie(BASE + '/tier3/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json'},
        body: JSON.stringify({
          tier1ReportId: reportId,
          questionnaireAnswers: tier3AnswersRef.current,
          fromPoints: usePoints || undefined,
          redeemCode: tier3RedeemCodeUsedRef.current || undefined,
          facePhotoKey: livePhotoKey || undefined})});
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 403 && (data.error === 'no_redeem_code' || data.error === 'redeem_code_used')) {
          setTier3RedeemCodeUsed(null);
          tier3RedeemCodeUsedRef.current = null;
          setTier3Error('暂无解锁资格，请使用积分或兑换码解锁后重试');
        } else if (data.retryable) {
          setTier3Error('生成失败，未消耗资格，可重新尝试');
        } else {
          setTier3Error(data?.error || '请求失败 ' + res.status);
        }
        // 成功才扣费模型：生成前未扣积分，生成失败无需退回（本就没有扣过）
      } else {
        // 若 AI 未给出具体商品名（productRecs 各维度为空），补一套默认品类骨架，
        // 保证二层「查看推荐商品」有内容可匹配；已命名则保持 AI 原样
        let finalContent = data.content || {};
        const recs = finalContent.productRecs;
        const isEmptyRecs = !recs || Object.keys(recs).length === 0 ||
          Object.values(recs).every((v) => !Array.isArray(v) || v.length === 0);
        if (isEmptyRecs) {
          finalContent = {
            ...finalContent,
            productRecs: {
              base: [{ name: "气垫粉底", reason: "轻薄持妆，通勤百搭" }],
              eyes: [{ name: "大地色眼影盘", reason: "自然提神" }, { name: "眼线胶笔", reason: "放大眼睛" }],
              lips: [{ name: "豆沙色唇釉", reason: "提升气色" }],
              cheeks: [{ name: "膏状腮红", reason: "自然红润" }]}};
        }
        setTier3Content(finalContent);
        // 成功才扣费：报告生成成功 → 扣 6 积分（按报告 id 去重，天然防重复扣；失败则不动积分）
        if (data.id && usePoints) {
          pointsApi.consumeReport(data.id).then((c) => {
            if (typeof c.balance === 'number') setTier3PointsBalance(c.balance);
          }).catch(() => {});
        }
        setTier3ContentPhotoUrl(tier3PhotoKeyLiveRef.current ? "/api/r2-proxy?key=" + encodeURIComponent(tier3PhotoKeyLiveRef.current) + "&bucket=temp" : null);
        // 记住报告 id + 收起“查看报告”态；生成完立即后台补全淘宝商品（点灯泡时有数据，不卡）
        setTier3ReportId(data.id || null);
        setTier3ReportViewOpen(false);
        // 兑换码已消耗，清掉 redeemCodeId（不可重复使用）
        setTier3RedeemCodeUsed(null);
        tier3RedeemCodeUsedRef.current = null;
        if (data.id && tier3EnrichFiredRef.current !== data.id) {
          tier3EnrichFiredRef.current = data.id;
          setTier3EnrichPending(true);
          fetchWithCookie(BASE + '/tier3/enrich-products', { method: 'POST', headers: { 'Content-Type': 'application/json'}, body: JSON.stringify({ reportId: data.id }) })
            .then((r) => (r.ok ? r.json() : null))
            .then((en) => { if (en && en.productRecs) setTier3Content((prev) => (prev ? { ...prev, productRecs: en.productRecs } : prev)); })
            .catch(() => {})
            .finally(() => setTier3EnrichPending(false));
        }
        // 本次生成已消耗资格（积分/兑换码各扣各的），清掉内存态；
        // 本次会话内的积分解锁状态保留（tier3PointsUnlocked）；但积分是"每次都要扣"，刷新后不再恢复资格。
        tier3PointsGrantedRef.current = false;
        // 兑换码已消耗，清掉 redeemCodeId（不可重复使用）
        setTier3RedeemCodeUsed(null);
        tier3RedeemCodeUsedRef.current = null;
        // 生成成功后回传的最新余额（本端 generate 已 proxy 读 auth-center），刷新"扣完积分"数字
        if (typeof data.balance === 'number') setTier3PointsBalance(data.balance);
        // AI 妆效图：generate 快速返回（不在服务端出图），立即触发后台生成 + 20s 轮询拿图。
        // ① POST /tier3/ai-image（幂等，已有图秒回；Agnes 2-key 轮换 + DashScope 兜底）
        // ② 20s 后 GET /tier3/report-id 查 DB ai_image_url，每 3s 再查一次，共 3 次；
        //    3 次都没图 → 设 aiImageExhausted=true，占位显示"重新生成妆后图"按钮。
        if (data.id && data.facePhotoKey) {
          tier3FacePhotoKeyRef.current = data.facePhotoKey;
          tier3AiImageFiredRef.current = data.id;
          setTier3AiImageExhausted(false);
          const aiBody = JSON.stringify({ reportId: data.id, photoKey: data.facePhotoKey });
          // ① 立即触发后台生成（90s 窗口，幂等，已有图秒回）
          fetchWithCookie(BASE + '/tier3/ai-image', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: aiBody })
            .then((r) => r.ok ? r.json() : null)
            .then((ai) => {
              if (ai && ai.aiImageUrl) { setTier3AiImageUrl(ai.aiImageUrl); setTier3AiImageExhausted(false); }
              else if (ai && ai.retryAfterSec) { setTier3AiRateLimitLiftAt(Date.now() + ai.retryAfterSec * 1000); }
            })
            .catch(() => {});
          // ② 轮询节奏：前 5 次 3s 密轮 + 后 3 次 5/10/15s（图写进 D1 后 3s 内命中，体感立即看到）
          const pollAiImage = (attempt = 0, maxAttempts = 8) => {
            if (attempt >= maxAttempts) { setTier3AiImageExhausted(true); return; }
            const t = setTimeout(async () => {
              try {
                const rid = await fetchWithCookie(BASE + '/tier3/report-id?id=' + encodeURIComponent(data.id));
                const d = rid.ok ? await rid.json().catch(() => null) : null;
                if (d && d.aiImageUrl) { setTier3AiImageUrl(d.aiImageUrl); setTier3AiImageExhausted(false); return; }
              } catch { /* 轮询失败继续下一次 */ }
              pollAiImage(attempt + 1, maxAttempts);
            }, [3000,3000,3000,3000,3000,5000,10000,15000][attempt]); // 前 5 次 3s 密轮 + 后 3 次 5/10/15s（图一写 D1，3s 内命中）
            tier3AiImageRetryTimersRef.current.push(t);
          };
          pollAiImage();
        }
      }
    } catch (e) {
        setTier3Error('生成超时或服务异常，请点下方按钮重试');
    } finally {
      setTier3Generating(false);
    }
  }, [reportId, tier3Generating, tier3Answers, token]);

  const handleTier3Answer = useCallback((dimension, value) => {
    if (tier3TimerRef.current) {
      clearTimeout(tier3TimerRef.current);
      tier3TimerRef.current = null;
      }
    setTier3Answers((prev) => {
      const next = { ...prev, [dimension]: value }; if (dimension === 'reportGender') { delete next.makeupStyle; delete next.shareMakeup; } else if (dimension === 'makeupStyle') { delete next.shareMakeup; } // 换风格大类时清空已选细分妆容（第2题选项随大类联动）
        return next;
    });
    setTier3AnswerFlash(dimension);
    tier3TimerRef.current = setTimeout(() => {
        tier3TimerRef.current = null;
      setTier3AnswerFlash(null);
      setTier3CurrentQuestionIndex((prev) => {
        const next = prev + 1;
        // 问卷只有 5 题：全部答完（next >= 题数）后停在最后一题，不再翻页；页面渲染层据此切到照片上传步骤
        return next >= TIER3_QUESTIONS.length ? prev : next;
      });
    }, 320);
  }, [handleTier3DoSubmit, tier3CurrentQuestionIndex]);

  // 专属报告解锁：「购买」跳转中枢积分购买页 /buy-points（当前页同窗口跳转）
  const handleTier3Buy = useCallback(() => {
    window.location.href = 'https://auth.meijian.top/buy-points?from=' + encodeURIComponent(window.location.href);
  }, []);

  const handleTier3Redeem = useCallback(async () => {
    if (!tier3RedeemCode.trim() || tier3Redeeming || !token) return;
    setTier3Redeeming(true);
    setTier3Error(null);
    try {
      const res = await fetchWithCookie(BASE + '/tier3/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json'},
        body: JSON.stringify({ code: tier3RedeemCode.trim() })});
      const data = await res.json();
      if (res.ok && data.success) {
        // 记住核销成功的码（code 本身），生成时传给 /tier3/generate 的 redeemCode 字段
        const usedCode = data.redeemCode || tier3RedeemCode.trim();
        setTier3RedeemCodeUsed(usedCode);
        tier3RedeemCodeUsedRef.current = usedCode;
        setTier3RedeemCode('');
        // 兑换码核销成功 → 直接进入定制问卷
        setTier3ShowQuestionnaire(true);
        setTier3Error(null);
      } else {
        setTier3Error(data.message || '兑换失败');
      }
    } catch { setTier3Error('网络异常，请重试'); }
    finally { setTier3Redeeming(false); }
  }, [tier3RedeemCode, tier3Redeeming, token]);

  // 点“查看报告”：展开二层报告 + 若尚未触发则立即补全淘宝商品（兜底：正在匹配中…）
  const handleTier3ViewReport = useCallback(() => {
    setTier3ReportViewOpen(true);
    if (tier3ReportId && tier3EnrichFiredRef.current !== tier3ReportId) {
      tier3EnrichFiredRef.current = tier3ReportId;
      setTier3EnrichPending(true);
      fetchWithCookie(BASE + '/tier3/enrich-products', { method: 'POST', headers: { 'Content-Type': 'application/json'}, body: JSON.stringify({ reportId: tier3ReportId }) })
        .then((r) => (r.ok ? r.json() : null))
        .then((en) => { if (en && en.productRecs) setTier3Content((prev) => (prev ? { ...prev, productRecs: en.productRecs } : prev)); })
        .catch(() => {})
        .finally(() => setTier3EnrichPending(false));
    }
    // 妆后 AI 图：若此刻仍缺失，打开报告时再幂等补拉一次（服务端 3 次尝试、已有图秒回），
    // 让"点开报告"这个动作本身触发/刷新出图；重复点开不会重复生成（服务端幂等）。
    if (!tier3AiImageUrl && tier3ReportId && tier3FacePhotoKeyRef.current) {
      fetchWithCookie(BASE + '/tier3/ai-image', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reportId: tier3ReportId, photoKey: tier3FacePhotoKeyRef.current }) })
        .then((r) => (r.ok ? r.json() : null))
        // 返回的是完整已编码 /api/r2-proxy?key=... URL，直接用（不再包一层）
        .then((ai) => { if (ai && ai.aiImageUrl) setTier3AiImageUrl(ai.aiImageUrl); })
        .catch(() => {});
    }
  }, [tier3ReportId, token, tier3AiImageUrl]);

  // "重新生成妆后图" 核心：当前页内触发 /tier3/ai-image（服务端 90s 单次、池内逐 key 轮换、已有图秒回）。
  // P1 自愈：按钮 60s 冷却防连点轰炸 i2i 服务；失败后自动后台重试 2 次（30s/60s 间隔）再试，
  // 无需用户反复手点；成功后原地显示、清除冷却与重试。供主视图与个人中心档案两条路径复用。
  const doRegenTier3AiImage = useCallback(async ({ reportId, facePhotoKey, setBusy, onLoaded, scheduleRetry = true }) => {
    if (!reportId) return false;
    // P1：60s 冷却 —— 刚点过（或刚自动重试过）就不重复触发，避免把 i2i 服务打饱和
    const now = Date.now();
    if (now < tier3AiImageRegenCooldownRef.current) return false; // 仍在冷却期
    tier3AiImageRegenCooldownRef.current = now + 60000; // 本次触发后 60s 冷却
    setBusy(true);
    try {
      let fKey = facePhotoKey;
      if (!fKey) {
        try {
          const rid = await fetchWithCookie(BASE + '/tier3/report-id?id=' + encodeURIComponent(reportId));
          const d = rid.ok ? await rid.json() : {};
          fKey = d.facePhotoKey || null;
        } catch { fKey = null; }
      }
      if (!fKey) return false;
      const res = await fetchWithCookie(BASE + '/tier3/ai-image', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reportId, photoKey: fKey }),
      });
      const data = res.ok ? await res.json().catch(() => ({})) : {};
      if (data.aiImageUrl) {
        onLoaded(data.aiImageUrl);
        tier3AiImageRegenCooldownRef.current = 0; // 成功即清冷却
        setTier3AiRateLimitLiftAt(0); // 出图成功 → 熔断解除
        return true;
      }
      // 限流窗口：后端熔断返回 retryAfterSec → 记窗口截止时间，按钮置灰倒计时；自动重试也尊重窗口
      if (data.retryAfterSec) setTier3AiRateLimitLiftAt(Date.now() + data.retryAfterSec * 1000);
      // P1：本次失败（i2i_failed / 服务暂不可用）→ 自动后台重试 2 次（30s、60s 间隔），
      // 期间按钮解除 busy 但不再重复自动触发（避免叠加）；用户也可稍后手动再点一次。
      if (scheduleRetry) {
        // P1 顺序自动重试（30s 后第2次、完成后再 60s 第3次）：前一次完成才发起下一次，
        // 全程最多 1 路 /tier3/ai-image 在途，避免并行 3 路 × 6 key 把 i2i 打饱和。
        const baseDelays = [10000, 20000, 30000];
        // 限流窗口内：重试间隔取 max(默认, retryAfter) 封顶 120s——不给窗口续命、不连打 Agnes
        const rlSec = data.retryAfterSec ? Math.min(120, data.retryAfterSec) : 0;
        const retryDelays = baseDelays.map((d) => Math.max(d, rlSec * 1000));
        let retryStep = 0;
        const nextRetry = () => {
          if (retryStep >= retryDelays.length) return;
          const t = setTimeout(async () => {
            try {
              const r = await fetchWithCookie(BASE + '/tier3/ai-image', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reportId, photoKey: fKey }),
              });
              const d2 = r.ok ? await r.json().catch(() => ({})) : {};
              if (d2 && d2.aiImageUrl) { onLoaded(d2.aiImageUrl); tier3AiImageRegenCooldownRef.current = 0; return; }
              if (d2 && d2.retryAfterSec) setTier3AiRateLimitLiftAt(Date.now() + d2.retryAfterSec * 1000);
            } catch { /* 自动重试失败就安静结束，用户仍可在冷却后手动重试 */ }
            retryStep += 1; nextRetry();
          }, retryDelays[retryStep]);
          tier3AiImageRetryTimersRef.current.push(t);
        };
        nextRetry();
      }
      return false;
    } catch {
      return false;
    } finally {
      setBusy(false);
    }
  }, [token]);

  // 主视图（专属标签页）封装
  const handleTier3RegenAiImage = useCallback(async () => {
    if (tier3AiImageRegenBusy || !tier3ReportId) return;
    const ok = await doRegenTier3AiImage({
      reportId: tier3ReportId,
      facePhotoKey: tier3FacePhotoKeyRef.current,
      setBusy: setTier3AiImageRegenBusy,
      onLoaded: (url) => setTier3AiImageUrl(url),
    });
    if (!ok) setTier3Error('妆后图本次仍未生成成功，可稍后再点一次（不影响报告主体）');
  }, [tier3ReportId, tier3AiImageRegenBusy, doRegenTier3AiImage]);

  // 个人中心档案路径封装（刷新后报告进个人中心，点开报告卡即走这里）
  const handleArchiveRegenAiImage = useCallback(async () => {
    if (archiveAiImageRegenBusy || !archiveDetail?.id) return;
    const ok = await doRegenTier3AiImage({
      reportId: archiveDetail.id,
      facePhotoKey: archiveDetail.facePhotoKey || null,
      setBusy: setArchiveAiImageRegenBusy,
      onLoaded: (url) => setArchiveDetail((prev) => (prev && prev.id === archiveDetail.id ? { ...prev, aiImageUrl: url } : prev)),
    });
    if (!ok) setTier3Error('妆后图本次仍未生成成功，可稍后再点一次（不影响报告主体）');
  }, [archiveDetail, archiveAiImageRegenBusy, doRegenTier3AiImage]);

  const handleTier3Refresh = useCallback(() => {
    setTier3Content(null);
    setTier3Error(null);
    setTier3Answers({});
    setTier3ShowQuestionnaire(false);
    // 重置为未解锁状态：界面回到积分/兑换码解锁引导页
    setTier3PointsUnlocked(false);
    tier3PointsGrantedRef.current = false;
    tier3PointsConsumedPendingRef.current = false;
    // 重置 related_id，下次解锁会生成新的去重键（"每次解锁都要扣积分"）
    tier3RelatedIdRef.current = null;
    setTier3RedeemCodeUsed(null);
    tier3RedeemCodeUsedRef.current = null;
  }, []);

  // 专属报告分享：复用用户上传的美妆分享模板，把二维码换成当前报告链接
  const handleShareTier3 = useCallback(async () => {
    if (shareLoading) return;
    try {
      const [invite] = await Promise.all([
        fetchInviteInfo(token),
      ]);
      const inviteCode = invite?.inviteCode || '';
      // 优先用报告链接，其次用注册邀请链接
      const shareUrl = reportId
        ? BASE + '/report?id=' + encodeURIComponent(reportId)
        : (inviteCode ? 'https://auth.meijian.top/register?invite=' + encodeURIComponent(inviteCode) : window.location.href);
      // composeShareCard 内部用固定邀请注册链接；这里需要专属报告链接，手动合成
      const QRCode = await import('qrcode');
      const qr = document.createElement('canvas');
      await QRCode.toCanvas(qr, shareUrl, { width: 200, margin: 1, color: { dark: '#2d2d2d', light: '#ffffff' } });
      const loadTpl = (u) => new Promise((res, rej) => {
        const im = new Image();
        im.onload = () => res(im);
        im.onerror = () => rej(new Error('tpl load fail ' + u));
        im.crossOrigin = 'anonymous';
        im.src = u;
      });
      const candUrls = ['/share-card-template.jpg'];
      
      let tpl; let lastErr;
      for (const u of candUrls) {
        try { tpl = await loadTpl(u); break; } catch (e) { lastErr = e; }
      }
      if (!tpl) throw lastErr || new Error('模板图加载失败');
      const c = document.createElement('canvas');
      c.width = tpl.naturalWidth; c.height = tpl.naturalHeight;
      const ctx = c.getContext('2d');
      ctx.drawImage(tpl, 0, 0, c.width, c.height);
      const QR_SIZE = 180, QR_X = 516 - QR_SIZE / 2, QR_Y = 1340 - QR_SIZE / 2;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(QR_X - 8, QR_Y - 8, QR_SIZE + 16, QR_SIZE + 16);
      ctx.drawImage(qr, QR_X, QR_Y, QR_SIZE, QR_SIZE);
      const blob = await new Promise((res, rej) => c.toBlob((b) => b ? res(b) : rej(new Error('合成失败')), 'image/png', 1.0));
      setShareDone(true);
      await shareImage(blob);
    } catch (e) {
      console.error('[ReportPage] tier3 分享异常:', e);
    } finally {
      setShareLoading(false);
    }
  }, [shareLoading, reportId, token]);

  const handleShareReport = useCallback(async () => {
    if (shareLoading || !reportId) return;
    setShareLoading(true);
    setShareDone(false);
    try {
      const res = await fetchWithCookie(BASE + '/tier1/share', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reportId })});
      if (!res.ok) {
        if (res.status === 400 || res.status === 429) {
          const errData = await res.json().catch(() => ({}));
          if (errData?.error === 'daily_limit_exceeded') {
            setShareDailyLimitExceeded(true);
            return;
          }
        }
        throw new Error('分享接口请求失败');
      }
      const { shareUrl } = await res.json();
      shareUrlRef.current = shareUrl;
      const QRCode = await import('qrcode');
      await QRCode.default.toCanvas(qrCanvasRef.current, shareUrl, {
        width: 160, margin: 1, color: { dark: '#2d2d2d', light: '#ffffff' }});
      await new Promise((r) => setTimeout(r, 50));
      const html2canvas = (await import('html2canvas')).default;
      const cardEl = shareCardRef.current;
      if (!cardEl) throw new Error('分享卡片 DOM 未就绪');
      const canvas = await html2canvas(cardEl, { backgroundColor: null, scale: 2, useCORS: true, allowTaint: true, logging: false });
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png', 1.0));
      if (!blob) throw new Error('截图生成失败');
      try {
        const file = new File([blob], '美妆分析报告.png', { type: 'image/png' });
        if (navigator.share) {
          await navigator.share({ title: '我的美妆分析报告', text: '看看我的美妆分析结果！\\n' + shareUrl, files: [file] });
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = '美妆分析报告.png'; a.click();
          URL.revokeObjectURL(url);
        }
      } catch {}
      setShareDone(true);
    } catch (err) { console.error('[ReportPage] 分享异常:', err); }
    finally { setShareLoading(false); }
  }, [shareLoading, reportId, token]);

  const handleUnlockImage = useCallback(() => {
    if (!reportId || imgUnlockLoading || unlockBusyRef.current) return;
    setAdMode('image'); setShowAd(true); setImgResult(null); setRetryable(false);
    unlockBusyRef.current = true;
  }, [reportId, imgUnlockLoading]);

  const handleAdFinish = useCallback(async () => {
    setShowAd(false); setImgUnlockLoading(true);
    try {
      const res = await fetchWithCookie(BASE + '/tier2/unlock-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reportId: tier2Status?.tier2ReportId || reportId })});
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) setImgResult({ reason: 'auth_expired', message: '登录状态已过期，请重新登录' });
        else setImgResult({ reason: 'unknown', message: data?.error || '解锁失败' });
        return;
      }
      if (data.unlocked && data.imageUrl) setImgResult({ imageUrl: data.imageUrl });
      else if (data.reason === 'feature_disabled') setImgResult({ reason: 'feature_disabled', message: data.message || 'AI 妆效图功能已暂停' });
      else if (data.reason === 'referral_not_confirmed') setImgResult({ reason: 'referral_not_confirmed' });
      else if (data.reason === 'daily_limit_exceeded') setImgResult({ reason: 'daily_limit_exceeded' });
      else if (data.reason === 'ai_generation_failed' || data.retryable) { setImgResult({ reason: 'ai_generation_failed' }); setRetryable(true); }
      else setImgResult({ reason: 'unknown', message: '未知响应' });
    } catch (e) { setImgResult({ reason: 'network_error', message: e.message || '网络异常' }); setRetryable(true); }
    finally { setImgUnlockLoading(false); unlockBusyRef.current = false; }
  }, [reportId, token]);

  const handleRetryUnlock = useCallback(() => {
    if (!reportId) return;
    setAdMode('image'); setShowAd(true); setImgResult(null); setRetryable(false);
    unlockBusyRef.current = true;
  }, [reportId]);


  // --- 广告解锁进阶报告（独立报告，不依赖初识，仅消耗每日限额）：先播放广告，结束后调解锁接口，再上传照片生成 ---
  const handleStartTier2AdUnlock = useCallback(() => {
    if (adUnlockLoading || unlockBusyRef.current) return;
    setAdMode('tier2');
    setShowAd(true);
    unlockBusyRef.current = true;
  }, [adUnlockLoading]);

  // 分析期间并行的广告：播完即关，不触发后端（报告由轮询驱动，次数在分析成功完成后由后端计入）
  const handleAdFinishForTier2Gen = useCallback(() => {
    setShowAd(false);
  }, []);

  const handleAdFinishForTier2 = useCallback(async () => {
    setShowAd(false);
    setAdUnlockLoading(true);
    try {
      const res = await fetchWithCookie(BASE + '/tier2/unlock-by-ad', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json'},
        body: JSON.stringify({})});
      const data = await res.json();
      if (!res.ok || data?.error === 'daily_limit_exceeded') {
        if (data?.error === 'daily_limit_exceeded') {
          alert(data.message || '今日进阶报告次数已用完，明天再来吧');
        }
        return;
      }
      // 解锁成功：独立报告进入 pending，界面展示照片上传入口（上传后开始生成与轮询）
      setTier2Generation({ generationStatus: 'pending', tier2ReportId: data.tier2ReportId, sourceTier1ReportId: null, unlocked: true });
      setTier2Status({ generationStatus: 'pending', tier2ReportId: data.tier2ReportId, sourceTier1ReportId: null, unlocked: true });
    } finally {
      setAdUnlockLoading(false);
      unlockBusyRef.current = false;
    }
  }, [token]);

  // --- 重新拍摄解锁：看广告消耗每日 1 次名额（复用 /tier2/unlock-by-ad），解锁后展示照片上传 ---
  const handleStartRedoAdUnlock = useCallback(() => {
    if (adUnlockLoading || unlockBusyRef.current) return;
    setAdMode('tier2redo');
    setShowAd(true);
    unlockBusyRef.current = true;
  }, [adUnlockLoading]);

  const handleAdFinishForTier2Redo = useCallback(async () => {
    setShowAd(false);
    setAdUnlockLoading(true);
    try {
      const res = await fetchWithCookie(BASE + '/tier2/unlock-by-ad', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json'},
        body: JSON.stringify({})});
      const data = await res.json();
      if (!res.ok || data?.error === 'daily_limit_exceeded') {
        if (data?.error === 'daily_limit_exceeded') {
          alert(data.message || '今日进阶报告次数已用完，明天再来吧');
        }
        return;
      }
      // 解锁成功：展示重新拍摄上传入口（复用当前报告记录生成）
      setRedoTier2Id(data.tier2ReportId);
      setRedoUnlocked(true);
    } finally {
      setAdUnlockLoading(false);
      unlockBusyRef.current = false;
    }
  }, [token]);

  // --- 独立报告照片提交成功：进入生成中，开始轮询 ---
  const handleTier2PhotoStarted = useCallback((t2Id) => {
    if (!t2Id) return;
    tier2StuckSinceRef.current = null;
    tier2StuckRetriesRef.current = 0;
    setTier2Content(null);
    setTier2Generation((prev) => ({ ...(prev || {}), generationStatus: 'processing', tier2ReportId: t2Id }));
    setTier2Status((prev) => ({ ...(prev || {}), generationStatus: 'processing', tier2ReportId: t2Id }));
    // 分析期间并行播放广告：广告与 AI 生成同时进行；广告结束若报告未出则停留在"生成中"兜底动画
    setAdMode('tier2gen');
    setShowAd(true);
    setRedoUnlocked(false);
  }, []);

  // --- 生成失败后重新生成（仅限关联初识报告的历史数据；不消耗广告解锁次数）
  // 独立报告走"重新上传照片"入口，不在此处触发，避免无数据空生成 ---
  const handleRetryTier2Generate = useCallback(() => {
    const gen = tier2GenerationRef.current;
    if (!gen?.tier2ReportId || !gen.sourceTier1ReportId) return;
    tier2StuckSinceRef.current = null;
    tier2StuckRetriesRef.current = 0;
    setTier2Generation({ ...gen, generationStatus: 'processing' });
    setTier2Status({ ...gen, generationStatus: 'processing' });
    fetchWithCookie(BASE + '/tier2/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json'},
      body: JSON.stringify({ reportId: gen.tier2ReportId })}).catch(() => {});
  }, [token]);


  const initReport = tier1Report;
  console.log('[ReportPage] initReport:', initReport, 'reportId:', reportId, 'reportValid:', reportValid);
  const resolvedResults = RESULT_ITEMS.map((item) => {
    const value = initReport?.[item.key] ?? item.placeholder;
    const compliment = getCompliment(item.key, value);
    return { ...item, value, compliment };
  });
  const personaTags = initReport?.personaTags ? [initReport.personaTags] : ['温柔知性风，适合日常淡妆', '清透裸妆感，凸显自然美'];
  const highlightText = initReport?.highlight ?? '你的五官比例协调，笑起来很有感染力';

  const t2 = tier2Content;
  const tier2FacePhotoUrl = tier2FacePhotoKey ? '/api/r2-proxy?key=' + encodeURIComponent(tier2FacePhotoKey) + '&bucket=perm' : null;

  // 个人中心专属报告到期预警：到期时间 <= 5 天时标红
  const tier3ExpireMs = myTier3?.expireAt ? myTier3.expireAt * 1000 : null;
  const expiringSoon = tier3ExpireMs ? (tier3ExpireMs - Date.now()) <= 5 * 24 * 60 * 60 * 1000 : false;

  return (
    <div className="report-page">
        <GenderConfirmCard onReady={setHubProfile} />
        <div ref={shareCardRef} className="share-card" style={{ position: 'absolute', left: -9999, top: 0 }}>
          <div className="share-card-photo-wrap">
            {preview ? <img className="share-card-photo" src={preview} alt="你的照片" /> : <div className="share-card-photo-placeholder">📷</div>}
          </div>
          <div className="share-card-tags">
            {personaTags.map((tag, i) => <span key={i} className="share-card-tag">{tag}</span>)}
          </div>
          <div className="share-card-highlight"><p className="share-card-highlight-text">{highlightText}</p></div>
          <div className="share-card-qr-wrap"><canvas ref={qrCanvasRef} className="share-card-qr" /><p className="share-card-qr-hint">扫码查看我的分析报告</p></div>
        </div>

        <div className="report-header">
          <button className="report-back-btn" onClick={navigateBack}>‹ 返回</button>
          <span className="report-title">美妆分析报告</span>
          <button className="report-archive-btn" onClick={() => setShowArchive(true)}>个人中心</button>
        </div>

        <div className="report-tabs">
          {['初识', '进阶', '专属'].map((tab) => (
            <button key={tab} className={'report-tab' + (activeTab === tab ? ' report-tab--active' : '')} onClick={() => setActiveTab(tab)}>{tab}</button>
          ))}
        </div>

        {/* 初识 */}
        {activeTab === '初识' && (
          <div className="report-tab-content">
            {reportValid === null && reportId ? (
              <div className="report-loading">正在验证报告...</div>
            ) : reportValid === false || !initReport ? (
              <>
                <CapturePhotoUpload
                  preview={preview}
                  compact
                  onComplete={(rid, reportData, previewData) => {
                    setTier1Report(reportData);
                    setPreview(previewData || null);
                    window.history.pushState({ reportId: rid, preview: previewData }, "", "/report?id=" + encodeURIComponent(rid));
                    window.dispatchEvent(new PopStateEvent("popstate"));
                  }}
                />
                <button className="capture-influencer-btn" onClick={() => window.location.href="/influencer-apply"} title="达人入驻">✨ 达人入驻</button>
              </>
            ) : (
              <>
                <div className="report-hero">
                  {preview ? <img className="report-hero-photo" src={preview} alt="你的照片" style={{ aspectRatio: '3/4' }} onClick={() => setOpenPhoto(preview)} /> : <div className="report-hero-photo-placeholder">📷</div>}
                  <div className="report-hero-tags">
                    {personaTags.map((tag, i) => <span key={i} className="report-hero-tag">{tag}</span>)}
                  </div>
                </div>
                <div className="report-section">
                  <h2 className="report-section-title">分析结果</h2>
                  <div className="report-grid">
                    {resolvedResults.map((item) => (
                      <div key={item.key} className="report-item">
                        <span className="report-item-icon">{item.icon}</span>
                        <div className="report-item-body">
                          <span className="report-item-label">{item.label}</span>
                          <span className="report-item-value">{item.value}</span>
                          <span className="report-item-compliment">— {item.compliment}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="report-section">
                  <h2 className="report-section-title">✨ 你的亮点</h2>
                  <div className="report-highlight-card">
                    <p className="report-highlight-text">{highlightText}</p>
                  </div>
                </div>
                {reuploadTier1 ? (
                  <>
                    <div className="report-reupload-section">
                      <p className="report-reupload-hint">重新上传照片后，将基于新照片生成新的初识报告</p>
                      <button className="report-reupload-btn" onClick={() => setReuploadTier1(false)}>← 返回查看当前报告</button>
                    </div>
                    <CapturePhotoUpload
                      preview={preview}
                      compact
                      onComplete={(rid, reportData, previewData) => {
                        setTier1Report(reportData);
                        setPreview(previewData || null);
                        setReuploadTier1(false);
                        window.history.pushState({ reportId: rid, preview: previewData }, "", "/report?id=" + encodeURIComponent(rid));
                        window.dispatchEvent(new PopStateEvent("popstate"));
                      }}
                    />
                  </>
                ) : (
                  <div className="report-reupload-section">
                    <button className="report-reupload-btn" onClick={handleReuploadTier1}>🔄 重新上传照片</button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* 进阶 */}
        {activeTab === '进阶' && (
          <div className="report-tab-content">
            {tier2Content ? (
              <>
                <Tier2Result content={tier2Content} isMock={!tier2Content} btnStyle={{background: btnColor}} onUnlockImage={handleAdFinish} facePhotoUrl={tier2FacePhotoUrl} />
                <div className="report-redo-upload">
                  <div className="report-unlock-prompt">
                    <div className="report-unlock-icon">🔄</div>
                    <p className="report-unlock-text">重新拍摄生成进阶报告</p>
                    <p className="report-unlock-hint">分析期间可看广告；生成失败不扣次数</p>
                    {tier2Generation?.tier2ReportId ? (
                      <Tier2PhotoUpload
                        compact
                        tier2ReportId={tier2Generation.tier2ReportId}
                        onStarted={handleTier2PhotoStarted}
                        title="请上传一张清晰的正面照片"
                      />
                    ) : null}
                  </div>
                </div>
              </>
            ) : tier2Generation?.generationStatus === 'processing' ? (
              <div className="report-loading">
                <div className="t2-spinner" />
                <p>AI 正在生成进阶报告（约 2-4 分钟），请稍候…</p>
              </div>
            ) : tier2Generation?.generationStatus === 'failed' ? (
              <div className="report-unlock-prompt">
                <div className="report-unlock-icon">⚠️</div>
                <p className="report-unlock-text">进阶报告生成失败</p>
                <p className="report-unlock-hint">失败不扣次数，重新上传照片即可再次生成</p>
                {tier2Generation?.tier2ReportId ? (
                  <Tier2PhotoUpload
                    compact
                    tier2ReportId={tier2Generation.tier2ReportId}
                    onStarted={handleTier2PhotoStarted}
                    title="重新上传照片"
                  />
                ) : (
                  <Tier2PhotoUpload
                    compact
                    onStarted={handleTier2PhotoStarted}
                    title="请上传一张清晰的正面照片"
                  />
                )}
              </div>
            ) : tier2Generation?.canGenerate === false ? (
              <div className="report-unlock-prompt">
                <div className="report-unlock-icon">🔒</div>
                <p className="report-unlock-text">今日进阶报告次数已用完</p>
                <p className="report-unlock-hint">每天 1 次，明天再来吧</p>
              </div>
            ) : (
              <div className="report-unlock-prompt">
                <div className="report-unlock-icon">📷</div>
                <p className="report-unlock-text">上传照片生成进阶报告</p>
                <Tier2PhotoUpload
                  compact
                  tier2ReportId={tier2Generation?.generationStatus === 'pending' ? tier2Generation?.tier2ReportId : undefined}
                  onStarted={handleTier2PhotoStarted}
                  title="请上传一张清晰的正面照片"
                />
                <p className="report-unlock-hint">进阶报告独立生成，无需先完成初识报告；分析期间可看广告</p>
              </div>
            )}
          </div>
        )}

        {activeTab === '专属' && (
          <div className="report-tab-content">
            {tier3LoadError && <div className="report-error"><p>{tier3LoadError}</p><button onClick={() => { setTier3LoadError(null); }}>重试</button></div>}
            {tier3PreviewLoading ? (
              <div className="report-loading">加载中...</div>
            ) : tier3Content ? (
              tier3ReportViewOpen ? (
                <Tier3Report
                photoUrl={tier3ContentPhotoUrl || (myTier3 && myTier3.photoUrl)}
                aiImageUrl={tier3AiImageUrl}
                showAiImage={tier3ShowAiImage}
                onRegenAiImage={handleTier3RegenAiImage}
                aiImageRegenBusy={tier3AiImageRegenBusy}
                aiImageExhausted={tier3AiImageExhausted}
                aiRateLimitLiftAt={tier3AiRateLimitLiftAt}
                content={{ ...tier3Content, _scenario: tier3Answers.scenario || (myTier3 && myTier3.scenario) || '今日妆容' }}
                onRefresh={handleTier3Refresh}
                onShare={handleShareTier3}
                shareLoading={shareLoading}
                shareDone={shareDone}
                enrichPending={tier3EnrichPending}
              />
              ) : (
                <div className="t3-view-report-wrap" style={{ textAlign: 'center', padding: '24px 16px' }}>
                  <p style={{ color: '#8a7f76', fontSize: 14, marginBottom: 16 }}>你的专属方案已生成 ✓</p>
                  <button className="t3-cta-btn" onClick={handleTier3ViewReport} style={{ width: '100%', maxWidth: 280, margin: '0 auto', display: 'block' }}>📄 查看报告</button>
                  {tier3EnrichPending ? <p style={{ color: '#b6a8a0', fontSize: 12, marginTop: 12 }}>正在最后总结…</p> : null}
                </div>
              )
            ) : !tier3ShowQuestionnaire ? (
              <div className="t3-unlock">
                <div className="t3-unlock-hero">
                  <div className="t3-unlock-badge">✦ PREMIUM REPORT ✦</div>
                  <h1 className="t3-unlock-title">专属深度美妆方案</h1>
                  <p className="t3-unlock-sub">AI 根据你的脸型、肤质与偏好，为你定制可落地的妆容步骤与产品清单</p>
                  {tier3PreviewLoading ? (
                    <p className="t3-unlock-hint">报告说明加载中...</p>
                  ) : tier3PreviewText ? (
                    <div className="t3-unlock-preview" dangerouslySetInnerHTML={{ __html: tier3PreviewText }} />
                  ) : null}
                </div>
                <div className="t3-unlock-tiles">
                  <button
                    className="t3-unlock-tile t3-unlock-tile--primary"
                    onClick={handleTier3Buy}
                  >
                    <span className="t3-unlock-tile-icon">💳</span>
                    <span className="t3-unlock-tile-label">购买积分解锁</span>
                    <span className="t3-unlock-tile-sub">前往中枢购买积分</span>
                  </button>
                  <button
                    className="t3-unlock-tile"
                    onClick={handleTier3UnlockByPoints}
                    disabled={tier3PointsUnlocked || tier3PointsConsume || tier3PointsBalance === null || tier3PointsBalance < UNLOCK_REPORT_AMOUNT}
                  >
                    <span className="t3-unlock-tile-icon">⭐</span>
                    <span className="t3-unlock-tile-label">
                      {tier3PointsUnlocked ? '已解锁（本次已扣 ' + UNLOCK_REPORT_AMOUNT + ' 积分）' : tier3PointsConsume ? '扣减中…' : tier3PointsBalance === null ? '积分加载中…' : tier3PointsBalance < UNLOCK_REPORT_AMOUNT ? '积分不足' : UNLOCK_REPORT_AMOUNT + ' 积分解锁'}
                    </span>
                    {tier3PointsBalance !== null && <span className="t3-unlock-tile-sub">当前余额 {tier3PointsBalance} 积分</span>}
                  </button>
                </div>
                <div className="t3-unlock-redeem">
                  <p className="t3-unlock-redeem-label">已有兑换码</p>
                  <div className="t3-unlock-redeem-row">
                    <input
                      className="t3-unlock-redeem-input"
                      type="text"
                      placeholder="输入10位兑换码"
                      value={tier3RedeemCode}
                      onChange={(e) => setTier3RedeemCode(e.target.value.toUpperCase())}
                      maxLength={10}
                    />
                    <button
                      className="t3-unlock-redeem-btn"
                      onClick={handleTier3Redeem}
                      disabled={tier3Redeeming || !tier3RedeemCode.trim()}
                    >
                      {tier3Redeeming ? '兑换中...' : '解锁'}
                    </button>
                  </div>
                  {tier3Error && <p className="report-q-error">{tier3Error}</p>}
                </div>
              </div>
            ) : !tier3ShowQuestionnaire ? (
              <div className="t3-unlock t3-unlock--ready">
                <div className="t3-unlock-hero">
                  <div className="t3-unlock-badge">✦ READY ✦</div>
                  <h1 className="t3-unlock-title">你已拥有专属报告资格</h1>
                  <p className="t3-unlock-sub">完成 6 道偏好问卷 + 上传一张照片，AI 即刻为你生成专属方案</p>
                </div>
                <button className="t3-cta-btn" onClick={() => setTier3ShowQuestionnaire(true)}>开始定制 →</button>
              </div>
            ) : !tier3Content && !tier3Generating ? (
              <div className="t3-quiz">
                <div className="t3-quiz-header">
                  <span className="t3-quiz-header-title">✨ 6 步定制你的专属方案</span>
                  <span className="t3-quiz-header-count">{tier3CurrentQuestionIndex + 1} / {TIER3_QUESTIONS.length}</span>
                </div>
                <div className="t3-quiz-progress">
                  <div
                    className="t3-quiz-progress-fill"
                    style={{ width: ((tier3CurrentQuestionIndex) / TIER3_QUESTIONS.length) * 100 + '%' }}
                  />
                </div>
                                <div className="t3-quiz-steps">
                  {TIER3_QUESTIONS.map((qq, i) => {
                    const stepLabel = { reportGender: '性别', makeupStyle: '大类', shareMakeup: '妆容', scenario: '场景', skillLevel: '水平', timeCost: '时间' }[qq.key] || qq.key;
                    const stepCls = i < tier3CurrentQuestionIndex ? 'done' : (i === tier3CurrentQuestionIndex ? 'now' : '');
                    return (
                      <div key={qq.key} className={'t3-step-chip ' + stepCls}>
                        <span className="t3-step-chip-n">{i + 1}</span>
                        <span className="t3-step-chip-label">{stepLabel}</span>
                      </div>
                    );
                  })}
                </div>
{tier3CurrentQuestionIndex > 0 && (
                  <button className="t3-quiz-back" onClick={handleTier3Back}>‹ 上一题</button>
                )}
                {(() => {
                  // 问卷全部完成 → 照片上传步骤
                  const allAnswered = ['reportGender', 'makeupStyle', 'shareMakeup', 'scenario', 'skillLevel', 'timeCost'].every((d) => tier3Answers[d]);
                  // 照片已上传（tier3PhotoKey 有值）但生成失败/未完成：展示"重新分析"按钮，避免卡在 Q4 无操作
                  if (allAnswered && !tier3Content && !tier3Generating) {
                    return (
                      <div className="t3-photo-step">
                        <div className="t3-photo-step-head">
                          <span className="t3-photo-step-emoji">📷</span>
                          <p className="t3-photo-step-title">{tier3PhotoKey ? '重新生成专属方案' : '上传照片开始分析'}</p>
                        </div>
                        <p className="t3-photo-step-hint">{tier3PhotoKey ? '照片已上传，可重新生成；若刚才失败请点下方按钮重试。' : '请上传一张清晰的正面照片，将为你生成专属分析'}</p>
                        {tier3PhotoKey && !tier3Photo && (
                          <div className="t3-photo-actions">
                            <button className="t3-cta-btn" onClick={handleTier3DoSubmit} disabled={tier3Generating}>
                              {tier3Generating ? '生成中…' : '🔁 重新生成'}
                            </button>
                          </div>
                        )}
                        {tier3Photo ? (
                          <div className="t3-photo-selected">
                            <img className="t3-photo-preview" src={tier3Photo} alt="照片预览" />
                            <div className="t3-photo-actions">
                              <button className="t3-cta-btn" onClick={handleTier3PhotoSubmit} disabled={tier3PhotoUploading}>
                                {tier3PhotoUploading ? '上传中…' : '开始分析'}
                              </button>
                              <button className="t3-photo-resel" onClick={() => { setTier3Photo(null); setTier3PhotoError(null); }}>重新选择</button>
                            </div>
                          </div>
                        ) : (
                          <div className="t3-photo-actions">
                            {typeof navigator !== 'undefined' && navigator.userAgent?.match(/(iPhone|iPad|iPod|Android)/i) ? (
                              <>
                                <button className="t3-photo-pick" onClick={handleTier3PhotoPickCamera}>📷 拍照</button>
                                <button className="t3-photo-pick" onClick={handleTier3PhotoPickGallery}>🖼 从相册选择</button>
                              </>
                            ) : (
                              <button className="t3-photo-pick" onClick={() => tier3PhotoInputRef.current?.click()}>📁 选择照片</button>
                            )}
                            <input ref={tier3PhotoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleTier3PhotoInputChange} />
                          </div>
                        )}
                        {(tier3PhotoError || tier3Error) && <p className="report-q-error">{tier3PhotoError || tier3Error}</p>}
                      </div>
                    );
                  }
                  const q = TIER3_QUESTIONS[tier3CurrentQuestionIndex];
                  if (!q) return null;
                  const isMale = GENDER_TO_LIB[tier3Answers.reportGender] === 'male';
                  let options;
                  if (q.key === 'reportGender') {
                    options = REPORT_GENDER_OPTIONS;
                  } else if (q.key === 'makeupStyle') {
                    options = isMale ? MALE_CATEGORY_LIST : (TIER3_FALLBACK_OPTIONS.makeupStyle || []);
                  } else if (q.key === 'shareMakeup') {
                    options = isMale
                      ? (MALE_CATEGORY_MAP[tier3Answers.makeupStyle] || MALE_CATEGORY_FALLBACK)
                      : (MAKEUP_SHARE_MAP[tier3Answers.makeupStyle] || MAKEUP_SHARE_FALLBACK);
                  } else if (q.key === 'scenario') {
                    options = TIER3_FALLBACK_OPTIONS[q.key] || [];
                  } else {
                    options = (tier3QuestionnaireOptions && tier3QuestionnaireOptions[q.key]) || TIER3_FALLBACK_OPTIONS[q.key] || [];
                  }if (!options.length) return null;
                  return (
                    <div className="t3-q-card" key={tier3CurrentQuestionIndex}>
                      <p className="t3-q-title">{q.title}</p>
                      <div className="t3-q-options">
                        {options.map((opt) => (
                          <button
                            key={opt}
                            className={[
                              't3-q-option',
                              tier3Answers[q.key] === opt ? 't3-q-option--active' : '',
                              tier3AnswerFlash === q.key ? 't3-q-option--flash' : '',
                            ].filter(Boolean).join(' ')}
                            onClick={() => handleTier3Answer(q.key, opt)}
                          >{opt}</button>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                {tier3Error && <p className="report-q-error">{tier3Error}</p>}
              </div>
            ) : tier3Generating ? (
              <div className="t3-generating">
                <div className="t3-generating-ring" />
                <p className="t3-generating-text">AI 正在根据你的偏好生成专属方案...</p>
                <p className="t3-generating-sub">通常需要几十秒，请稍候</p>
              </div>
            ) : null}
          </div>
        )}

          {showAd && <AdOverlay duration={AD_DURATION_SEC} onComplete={adMode === 'tier2gen' ? handleAdFinishForTier2Gen : adMode === 'tier2' ? handleAdFinishForTier2 : adMode === 'tier2redo' ? handleAdFinishForTier2Redo : handleAdFinish} />}
          {openPhoto && (
            <div className="photo-lightbox-overlay" onClick={() => setOpenPhoto(null)}>
              <img className="photo-lightbox-img" src={openPhoto} alt="放大预览" />
            </div>
          )}
        {showArchive && (
          <div className="archive-overlay" onClick={() => setShowArchive(false)}>
            <div className="archive-modal" onClick={(e) => e.stopPropagation()}>
              <div className="archive-modal-header">
                <span className="archive-modal-title">个人中心</span>
                <button className="archive-modal-close" onClick={() => setShowArchive(false)}>✕</button>
              </div>
              <div className="archive-modal-body">
                {myTier3Archives.length > 0 ? (
                  <>
                    <div className="pc-archive-header">
                      <span className="pc-archive-title">专属报告档案</span>
                      <span className="pc-archive-count">共 {myTier3Archives.length} 份</span>
                    </div>
                    {myTier3Archives.map((a) => {
                      const remainDays = a.expireAt ? Math.max(0, Math.ceil((a.expireAt * 1000 - cdNow) / 86400000)) : 0; const critical = !a.expired && remainDays <= 5;
                      const isOpen = archiveOpenId === a.id;
                      return (
                        <div key={a.id} className={"pc-report-card" + (a.expired ? " pc-report-card--expired" : "") + (isOpen ? " pc-report-card--open" : "")}>
                          <div className="pc-report-card-head" role="button" onClick={() => setArchiveOpenId(a.id)}>
                            {a.photoUrl ? (
                              // 档案列表缩略图懒加载：视口外的历史报告封面不再立即并发拉取，
                              // 缓解同域并发上限导致的排队（瀑布图里 300→850ms 的成因）
                              <img className="pc-report-photo" src={a.photoUrl} alt="报告照片" loading="lazy" decoding="async" />
                            ) : <div className="pc-report-photo pc-report-photo--empty">📷</div>}
                            <div className="pc-report-card-main">
                              <div className="pc-report-card-line">
                                <span className="pc-report-card-style">{a.style || a.scenario || "专属方案"}</span>
                                {a.expired ? <span className="pc-report-tag pc-report-tag--expired">已过期</span> : <span className={"pc-report-tag " + (critical ? "pc-report-tag--red" : "pc-report-tag--valid")}>有效<span className="pc-report-tag-cd"> 剩 {remainDays} 天</span></span>}
                              </div>
                              <div className="pc-report-card-sub">生成于 {formatExpireDate(a.createdAt)} · 查看完整报告</div>
                            </div>
                            <button type="button" className="pc-report-view-btn" onClick={(e) => { e.stopPropagation(); setArchiveOpenId(a.id); }}>查看详情</button>
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <div className="archive-empty">
                    <p>暂无专属报告</p>
                    <p className="archive-empty-hint">在「专属」标签解锁并生成专属报告后，将在此展示</p>
                    <button
                      className="archive-reupload-btn"
                      onClick={() => {
                        setShowArchive(false);
                        setActiveTab("专属");
                      }}
                    >
                      ✨ 去生成专属报告
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 专属报告全屏查看：从个人中心"查看详情"打开，整页可滚动展示；左上角返回 / 点背景 → 回到个人中心列表 */}
        {showArchive && archiveOpenId && (
          <div className="archive-report-fullscreen" onClick={() => setArchiveOpenId(null)}>
            <div className="archive-report-fullscreen-bar">
              <button type="button" className="archive-report-back-btn" onClick={() => setArchiveOpenId(null)}>‹ 返回</button>
              <span className="archive-report-title">专属报告</span>
              <span className="archive-report-title-spacer" />
            </div>
            <div className="archive-report-fullscreen-body" onClick={(e) => e.stopPropagation()}>
              {archiveDetailLoading && archiveOpenId ? (
                <p className="pc-report-detail-loading">报告详情加载中...</p>
              ) : archiveDetail && archiveDetail.id === archiveOpenId && archiveDetail.content ? (
                <Tier3Report
                  content={{ ...archiveDetail.content, _scenario: archiveDetail.scenario || archiveDetail.content._scenario || "今日妆容" }}
                  photoUrl={archiveDetail.photoUrl}
                  aiImageUrl={archiveDetail.aiImageUrl || null}
                  showAiImage={tier3ShowAiImage}
                  onRegenAiImage={handleArchiveRegenAiImage}
                  aiImageRegenBusy={archiveAiImageRegenBusy}
                  enrichPending={tier3EnrichPending}
                />
              ) : (
                <p className="pc-report-detail-loading">无法加载该报告详情</p>
              )}
            </div>
          </div>
        )}

    </div>
  );
}
